/**
 * CLIENT D'API DU POSTE DE TRAVAIL — le seul endroit qui parle au service.
 *
 * ⚠️ Le transport et la vérification publique vivent dans `api-noyau.ts` : la
 * page publique (§17) ne doit embarquer NI cet objet, NI la liste des routes de
 * l'atelier qu'il contient. Ce fichier les ré-exporte pour que rien n'ait à
 * changer ailleurs.
 */
export { ErreurApi, appeler, appelerFichier, appelerVerification, verifications } from './api-noyau';
export type { FichierRecu } from './api-noyau';
export type { EtatVerification, PointVerifie, Verification } from './api-noyau';
import { appeler, appelerFichier, appelerVerification, verifications } from './api-noyau';

/** Les paramètres de forme en query — UNE implémentation, partagée par les sept lectures du plan. */
const enForme = (f: Record<string, number>) =>
  Object.entries(f).map(([k, v]) => `&${k}=${v}`).join('');


export const api = {
  sante: () => appeler<Sante>('/health'),
  /**
   * §20-§25 PROSPECTIVE — le terrain et ses contraintes.
   *
   * ⚠️ AUCUN TYPE RECOPIÉ DEPUIS LE SERVICE. Les états (`mesure`,
   * `hors_couverture`, `relief_absent`, `emprise_invalide`) sont rendus tels
   * quels et l'écran les distingue : c'est précisément la confusion entre
   * « zéro mètre » et « pas de donnée » que ce module existe pour empêcher.
   */
  /**
   * §19 · §31-§36 · §37 INTELLIGENCE TERRITORIALE.
   *
   * ⚠️ AUCUN TYPE RECOPIÉ, ET SURTOUT PAS LES AVEUX. `aveux` et `sans_matiere`
   * voyagent tels que le service les rend : un écran qui les reformulerait
   * finirait par en perdre un, et un aveu perdu redevient un silence.
   */
  intelligence: {
    observation: (niveau?: number, parentId?: string) => {
      const q = new URLSearchParams();
      if (niveau !== undefined) q.set('niveau', String(niveau));
      if (parentId) q.set('parentId', parentId);
      return appeler<any>(`/intelligence/observation${q.size ? `?${q}` : ''}`);
    },
    radar: (niveau: number) => appeler<any>(`/intelligence/radar?niveau=${niveau}`),
    score: (divisionId: string) => appeler<any>(`/intelligence/score/${divisionId}`),
    signaux: () => appeler<any>('/intelligence/signaux'),
    etat: () => appeler<any>('/intelligence/etat'),
    rapport: (divisionId: string) => appeler<any>(`/intelligence/rapport/${divisionId}`),
    rafraichir: () => appeler<any>('/intelligence/rafraichir', { method: 'POST' }),
  },
  prospective: {
    couverture: () => appeler<CouvertureRelief>('/prospective/terrain/couverture'),
    altitude: (lat: number, lon: number) =>
      appeler<LectureAltitude>(`/prospective/terrain/altitude?lat=${lat}&lon=${lon}`),
    contraintes: (o: number, s: number, e: number, n: number) =>
      appeler<LectureContraintes>(
        `/prospective/contraintes?ouest=${o}&sud=${s}&est=${e}&nord=${n}`),
    programme: (o: number, s: number, e: number, n: number) =>
      appeler<LectureProgramme>(
        `/prospective/programme?ouest=${o}&sud=${s}&est=${e}&nord=${n}`),
    masterplan: (o: number, s: number, e: number, n: number, f: Record<string, number>) =>
      appeler<LectureMasterplan>(
        `/prospective/masterplan?ouest=${o}&sud=${s}&est=${e}&nord=${n}${enForme(f)}`),
    etapes: () => appeler<EtatEtapes>('/prospective/etapes'),
    infrastructures: (o: number, s: number, e: number, n: number, f: Record<string, number> = {}) =>
      appeler<LectureInfrastructures>(`/prospective/infrastructures?ouest=${o}&sud=${s}&est=${e}&nord=${n}${enForme(f)}`),
    mobilite: (o: number, s: number, e: number, n: number, f: Record<string, number> = {}) =>
      appeler<LectureMobilite>(`/prospective/mobilite?ouest=${o}&sud=${s}&est=${e}&nord=${n}${enForme(f)}`),
    quartiers: (o: number, s: number, e: number, n: number, f: Record<string, number> = {}) =>
      appeler<LectureQuartiers>(`/prospective/quartiers?ouest=${o}&sud=${s}&est=${e}&nord=${n}${enForme(f)}`),
    batiments: (o: number, s: number, e: number, n: number, f: Record<string, number> = {}) =>
      appeler<LectureBatiments>(`/prospective/batiments?ouest=${o}&sud=${s}&est=${e}&nord=${n}${enForme(f)}`),
    reseaux: (o: number, s: number, e: number, n: number, f: Record<string, number> = {}) =>
      appeler<LectureReseaux>(`/prospective/reseaux?ouest=${o}&sud=${s}&est=${e}&nord=${n}${enForme(f)}`),
    simulation: (o: number, s: number, e: number, n: number, f: Record<string, number> = {}) =>
      appeler<LectureSimulation>(`/prospective/simulation?ouest=${o}&sud=${s}&est=${e}&nord=${n}${enForme(f)}`),
    scenarios: {
      deposer: (c: { titre: string; mode: 'creer' | 'transformer' | 'simuler';
                     ouest: number; sud: number; est: number; nord: number;
                     parametres: Record<string, number>; hypotheses: string }) =>
        appeler<{ reference: string; creeLe: string; viable: boolean }>(
          '/prospective/scenarios', { method: 'POST', body: JSON.stringify(c) }),
      lister: () => appeler<{ total: number; scenarios: ScenarioResume[] }>('/prospective/scenarios'),
      fiche: (reference: string) =>
        appeler<FicheScenario>(`/prospective/scenarios/${encodeURIComponent(reference)}`),
      retirer: (reference: string) =>
        appeler<{ reference: string; statut: string }>(
          `/prospective/scenarios/${encodeURIComponent(reference)}/retirer`, { method: 'POST' }),
    },
  },
  session: {
    lire: () => appeler<Session>('/session'),
    /** Par où peut-on entrer — MESURÉ par le service, jamais récité par l'écran. */
    moyens: () => appeler<MoyensConnexion>('/session/moyens'),
    ouvrir: (jeton: string, codeInvitation?: string) =>
      appeler<Session>('/session', { method: 'POST', body: JSON.stringify({ jeton, codeInvitation }) }),
    fermer: () => appeler<{ ouverte: false }>('/session', { method: 'DELETE' }),
    oidc: {
      debut: () => appeler<{ url: string; expireDansS: number }>('/session/oidc/debut'),
      retour: (code: string, etat: string, codeInvitation?: string) =>
        appeler<Session>('/session/oidc/retour', {
          method: 'POST', body: JSON.stringify({ code, etat, codeInvitation }),
        }),
    },
  },
  /**
   * LOT B — L'ACTE COMMUNAL (attestation de cession, Art.3).
   *
   * ⚠️ L'ÉCRAN NE RÉCITE AUCUNE RÈGLE : il demande. `reglages()` rend les
   * vocabulaires (rôles, origines, pièces d'identité) et l'autorité compétente ;
   * `manques` est CALCULÉ PAR LA BASE (`fon.attestation_manques`) et suit chaque
   * réponse. Une seconde liste d'exigences tenue ici divergerait au premier
   * correctif appliqué d'un seul côté — et la divergence se verrait sous sa
   * pire forme : un acte annoncé complet que la base refuse de délivrer.
   */
  attestations: {
    reglages: () => appeler<ReglagesAttestation>('/attestations/reglages'),
    lister: (q: URLSearchParams) => appeler<ListeAttestations>(`/attestations?${q}`),
    fiche: (reference: string) =>
      appeler<FicheAttestation>(`/attestations/${encodeURIComponent(reference)}`),
    ouvrir: (corps: Record<string, unknown>) =>
      appeler<ReponseAttestation>('/attestations', { method: 'POST', body: JSON.stringify(corps) }),
    completer: (reference: string, corps: Record<string, unknown>) =>
      appeler<ReponseAttestation>(`/attestations/${encodeURIComponent(reference)}`,
        { method: 'PATCH', body: JSON.stringify(corps) }),
    ajouterPartie: (reference: string, corps: Record<string, unknown>) =>
      appeler<ReponseAttestation>(`/attestations/${encodeURIComponent(reference)}/parties`,
        { method: 'POST', body: JSON.stringify(corps) }),
    signer: (reference: string, partieId: string, corps: Record<string, unknown>) =>
      appeler<ReponseAttestation>(
        `/attestations/${encodeURIComponent(reference)}/parties/${partieId}/signer`,
        { method: 'POST', body: JSON.stringify(corps) }),
    pieces: (reference: string, corps: Record<string, unknown>) =>
      appeler<ReponseAttestation>(`/attestations/${encodeURIComponent(reference)}/pieces`,
        { method: 'POST', body: JSON.stringify(corps) }),
    affichage: (reference: string, corps: Record<string, unknown>) =>
      appeler<ReponseAttestation>(`/attestations/${encodeURIComponent(reference)}/affichage`,
        { method: 'POST', body: JSON.stringify(corps) }),
    opposition: (reference: string, corps: Record<string, unknown>) =>
      appeler<ReponseAttestation>(`/attestations/${encodeURIComponent(reference)}/opposition`,
        { method: 'POST', body: JSON.stringify(corps) }),
    acterSignature: (reference: string, motif?: string) =>
      appeler<ReponseAttestation>(`/attestations/${encodeURIComponent(reference)}/signer`,
        { method: 'POST', body: JSON.stringify({ motif }) }),
    delivrer: (reference: string, motif?: string) =>
      appeler<ReponseDelivrance>(`/attestations/${encodeURIComponent(reference)}/delivrer`,
        { method: 'POST', body: JSON.stringify({ motif }) }),
    annuler: (reference: string, motif: string) =>
      appeler<ReponseAttestation>(`/attestations/${encodeURIComponent(reference)}/annuler`,
        { method: 'POST', body: JSON.stringify({ motif }) }),
    /**
     * Art.3 — « le propriétaire requiert EN OUTRE l'immatriculation ».
     * ⚠️ `requisition()` dit ce que l'acte FOURNIT et ce qu'il faut encore
     * DEMANDER : le registre ne connaît pas l'état civil de l'acquéreur, et il
     * ne l'inventera pas en découpant une dénomination.
     */
    requisition: (reference: string) =>
      appeler<RequisitionDepuisActe>(`/attestations/${encodeURIComponent(reference)}/requisition`),
    requerir: (reference: string, corps: Record<string, unknown>) =>
      appeler<ReponseRequisition>(
        `/attestations/${encodeURIComponent(reference)}/requerir-immatriculation`,
        { method: 'POST', body: JSON.stringify(corps) }),
  },
  /**
   * LOT C — LE LIVRE FONCIER (Art.128 à 132).
   *
   * ⚠️ AUCUNE ROUTE D'INSCRIPTION DE DROIT ICI, et c'est la règle : une mention
   * qui publie un droit naît de la MUTATION, dans sa transaction. Un second
   * chemin d'écriture depuis l'écran donnerait un rang qui ne vient pas du
   * dépôt — c'est-à-dire une antidate.
   */
  livre: {
    tableau: () => appeler<TableauLivre>('/livre'),
    reglages: () => appeler<ReglagesLivre>('/livre/reglages'),
    feuilles: (q: URLSearchParams) => appeler<ListeFeuilles>(`/livre/feuilles?${q}`),
    feuille: (numeroTitre: string) =>
      appeler<FicheFeuille>(`/livre/feuilles/${encodeURIComponent(numeroTitre)}`),
    ouvrirFeuille: (corps: { numeroTitre: string; motif: string; bureauRegistre?: string }) =>
      appeler<ReponseLivre>('/livre/feuilles', { method: 'POST', body: JSON.stringify(corps) }),
    rectifier: (numeroTitre: string, corps: Record<string, unknown>) =>
      appeler<ReponseLivre>(`/livre/feuilles/${encodeURIComponent(numeroTitre)}/rectification`,
        { method: 'POST', body: JSON.stringify(corps) }),
    verserPiece: (numeroTitre: string, corps: Record<string, unknown>) =>
      appeler<ReponseLivre>(`/livre/feuilles/${encodeURIComponent(numeroTitre)}/pieces`,
        { method: 'POST', body: JSON.stringify(corps) }),
    delivrerCopie: (numeroTitre: string, corps: Record<string, unknown>) =>
      appeler<ReponseCopie>(`/livre/feuilles/${encodeURIComponent(numeroTitre)}/copie`,
        { method: 'POST', body: JSON.stringify(corps) }),
    clore: (numeroTitre: string, motif: string) =>
      appeler<ReponseLivre>(`/livre/feuilles/${encodeURIComponent(numeroTitre)}/clore`,
        { method: 'POST', body: JSON.stringify({ motif }) }),
    depots: (q: URLSearchParams) => appeler<ListeDepots>(`/livre/depots?${q}`),
    deposer: (corps: Record<string, unknown>) =>
      appeler<ReponseDepot>('/livre/depots', { method: 'POST', body: JSON.stringify(corps) }),
    suiteDepot: (id: string, corps: { suite: 'rejete' | 'retire'; motif: string }) =>
      appeler<ReponseLivre>(`/livre/depots/${id}/suite`, { method: 'POST', body: JSON.stringify(corps) }),
    parcelle: (identifiant: string) =>
      appelerVerification<FeuilleDuFonds>(`/livre/parcelle/${encodeURIComponent(identifiant)}`),
  },
  foncier: {
    parcelle: (identifiant: string) =>
      appeler<SituationFonciere>(`/foncier/parcelle/${encodeURIComponent(identifiant)}`),
    chercherTitulaires: (q: string) =>
      appeler<{ titulaires: TitulaireResume[]; message?: string }>(
        `/foncier/titulaires?q=${encodeURIComponent(q)}`),
    titulaire: (id: string) => appeler<FicheTitulaire>(`/foncier/titulaires/${id}`),
    inscrireRestriction: (corps: DemandeRestriction) =>
      appeler<{ restriction: string; message: string }>('/foncier/restrictions',
        { method: 'POST', body: JSON.stringify(corps) }),
    leverRestriction: (id: string, motif: string) =>
      appeler<{ levee: boolean; message: string }>(`/foncier/restrictions/${id}/lever`,
        { method: 'POST', body: JSON.stringify({ motif }) }),
  },
  plans: {
    variantes: () => appeler<VariantesPlan>('/plans/variantes'),
    /** ⚠️ Rend une URL, pas du contenu : le plan est un DOCUMENT. On le donne
     *  au navigateur, qui sait l'afficher, l'imprimer à l'échelle et l'enregistrer. */
    url: (identifiant: string,
          p: { variante: string; format: string; echelle?: number; apercu?: boolean }) => {
      const q = new URLSearchParams({ variante: p.variante, format: p.format });
      if (p.echelle) q.set('echelle', String(p.echelle));
      /**
       * ⚠️ `apercu` MANQUAIT, ET L'APERÇU D'ÉCRAN ÉTAIT CASSÉ DEPUIS LE
       * 12/09/2026. Ce jour-là le plan est passé par DocTrust : la route rend
       * un PDF par défaut, et ne rend du SVG que sous `?apercu=1`. Cette
       * fonction ne l'a jamais posé — l'écran servait donc un PDF à un
       * `<object type="image/svg+xml">`, qui déclare un type que le flux n'a
       * pas. Rien n'échouait bruyamment : selon le navigateur, le cadre
       * restait blanc ou affichait un lecteur PDF là où le code annonçait un
       * dessin vectoriel.
       */
      if (p.apercu) q.set('apercu', '1');
      return `/plans/parcelle/${encodeURIComponent(identifiant)}?${q}`;
    },
    /** Les contrôles AVANT génération : « prêt » ou « bloqué », et pourquoi. */
    controles: (identifiant: string) =>
      appeler<ControlesPlan>(`/plans/parcelle/${encodeURIComponent(identifiant)}/controles`),
    /** §11/§16 — les plans DÉLIVRÉS de cette parcelle, et leur géométrie. */
    editions: (identifiant: string) =>
      appeler<EditionsPlan>(`/plans/parcelle/${encodeURIComponent(identifiant)}/editions`),
  },
  /**
   * §3 — LE PLAN DE SITUATION. Trois fenêtres emboîtées : territoriale,
   * communale, environnement immédiat.
   *
   * ⚠️ ROUTES SÉPARÉES DE `/plans`, ET CE N'EST PAS UN RANGEMENT. Un plan
   * cadastral et un plan de situation ne sont pas deux variantes du même
   * document : le premier montre UNE fenêtre à grande échelle avec cotes et
   * coordonnées, le second TROIS fenêtres et aucune cote. Leurs versions se
   * comptent séparément, et c'est voulu — « V2 » du plan cadastral et « V2 »
   * du plan de situation désignent deux papiers différents.
   */
  situation: {
    /** ⚠️ Comme pour les plans : le défaut par défaut est le PDF. `apercu` rend le SVG. */
    url: (identifiant: string, p?: { rayonM?: number; apercu?: boolean }) => {
      const q = new URLSearchParams();
      if (p?.rayonM) q.set('rayonM', String(p.rayonM));
      if (p?.apercu) q.set('apercu', '1');
      const s = q.toString();
      return `/situation/parcelle/${encodeURIComponent(identifiant)}${s ? `?${s}` : ''}`;
    },
    controles: (identifiant: string) =>
      appeler<ControlesSituation>(`/situation/parcelle/${encodeURIComponent(identifiant)}/controles`),
    editions: (identifiant: string) =>
      appeler<EditionsSituation>(`/situation/parcelle/${encodeURIComponent(identifiant)}/editions`),
  },
  /**
   * §5 — LE DOCUMENT D'ARPENTAGE. État avant → Opération → État après.
   *
   * ⚠️ PAS DE ROUTE « contrôles » ICI, ET C'EST VOULU. Les contrôles avant
   * génération portent sur la capacité à DESSINER une parcelle ; un document
   * d'arpentage dessine un PASSAGE entre deux états, et son défaut typique
   * n'est pas une géométrie invalide mais une opération dont le registre ne
   * sait dire ni l'auteur ni la justification. Ce manque-là ne se bloque pas,
   * il s'IMPRIME — d'où `etat`, qui le montre à l'écran avant l'édition.
   */
  arpentage: {
    url: (identifiant: string, p?: { apercu?: boolean }) =>
      `/arpentage/parcelle/${encodeURIComponent(identifiant)}${p?.apercu ? '?apercu=1' : ''}`,
    etat: (identifiant: string) =>
      appeler<EtatArpentage>(`/arpentage/parcelle/${encodeURIComponent(identifiant)}/etat`),
    editions: (identifiant: string) =>
      appeler<EditionsArpentage>(`/arpentage/parcelle/${encodeURIComponent(identifiant)}/editions`),
  },
  /** §37-§43 — les modes métier et le plan de travail. */
  modes: {
    catalogue: () => appeler<CatalogueModes>('/modes'),
    plan: (code: string) => appeler<PlanTravail>(`/modes/${encodeURIComponent(code)}/plan`),
  },
  /** §10-§11 — le marché immobilier et le moteur DVF. */
  immobilier: {
    statistiques: (q: URLSearchParams) => appeler<ReponseStatistiques>(`/immobilier/statistiques?${q}`),
    transactions: (q: URLSearchParams) => appeler<ReponseTransactions>(`/immobilier/transactions?${q}`),
    tendances: (q: URLSearchParams) => appeler<ReponseTendances>(`/immobilier/tendances?${q}`),
    anomalies: (q: URLSearchParams) => appeler<ReponseAnomalies>(`/immobilier/anomalies?${q}`),
    comparables: (q: URLSearchParams) => appeler<ReponseComparables>(`/immobilier/comparables?${q}`),
    secteur: (q: URLSearchParams) => appeler<ReponseSecteur>(`/immobilier/secteur?${q}`),
    parcelle: (identifiant: string) =>
      appeler<ChaineParcelle>(`/immobilier/parcelle/${encodeURIComponent(identifiant)}`),
  },
  /** §12 — le registre des copropriétés. */
  coproprietes: {
    chercher: (q: URLSearchParams) => appeler<ListeCoproprietes>(`/coproprietes?${q}`),
    fiche: (identifiant: string) =>
      appeler<FicheCopropriete>(`/coproprietes/${encodeURIComponent(identifiant)}`),
  },
  /** §13 — le Property-Fiscal Graph. */
  fiscal: {
    parcelle: (identifiant: string) =>
      appeler<GrapheFiscal>(`/fiscal/parcelle/${encodeURIComponent(identifiant)}`),
    synthese: () => appeler<SyntheseFiscale>('/fiscal/synthese'),
    resoudre: (q: URLSearchParams) => appeler<ResolutionFiscale>(`/fiscal/resoudre?${q}`),
  },
  comptes: {
    liste: (inactifs = false) => appeler<{ comptes: CompteAdmin[] }>(`/comptes?inactifs=${inactifs}`),
    invitations: () => appeler<{ invitations: InvitationAdmin[] }>('/comptes/invitations'),
    inviter: (corps: DemandeInvitation) =>
      appeler<InvitationEmise>('/comptes/invitations', { method: 'POST', body: JSON.stringify(corps) }),
    habiliter: (compteId: string,
                corps: { role: string; portee: 'nationale' | 'division'; porteeDivisionId?: string; motif: string }) =>
      appeler<{ accordee: boolean; message: string }>(`/comptes/${compteId}/habilitations`,
        { method: 'POST', body: JSON.stringify(corps) }),
    retirerRole: (compteId: string, role: string, motif: string) =>
      appeler<{ retiree: boolean; message: string }>(
        `/comptes/${compteId}/habilitations/${encodeURIComponent(role)}?motif=${encodeURIComponent(motif)}`,
        { method: 'DELETE' }),
    activite: (compteId: string, actif: boolean, motif: string) =>
      appeler<{ actif: boolean; message: string }>(`/comptes/${compteId}/actif`,
        { method: 'POST', body: JSON.stringify({ actif, motif }) }),
  },
  parcelles: {
    fiche: (identifiant: string) => appeler<FicheParcelle>(`/parcelles/${encodeURIComponent(identifiant)}`),

    /**
     * ⚠️ CE QUE LE REGISTRE SAIT DÉJÀ — §2.3 : « une affaire naît d'une
     * parcelle, DÉSIGNÉE SUR LA CARTE, jamais en tapant un identifiant ».
     * Mesuré le 23/09/2026 : le guichet demandait l'identifiant « si le
     * requérant le connaît » et laissait le reste de l'adresse libre. Un dépôt
     * d'essai a ainsi déclaré « Conakry / Matoto / Tombola » pour un fonds de
     * **Boké-Centre / Yomboya** — et l'affaire est partie à 250 km, sans qu'un
     * seul contrôle ne le voie.
     */
    pourDemande: (identifiant: string) =>
      appeler<PreRemplissageDemande>(`/parcelles/${encodeURIComponent(identifiant)}/pour-demande`),
    /**
     * ⚠️ `sansGeometrie` N'EST PAS UN CONFORT. Mesuré le 24/09/2026 : la
     * géométrie pèse **80 %** de la réponse (691 Ko sur 853 pour 500 entités,
     * 3,34 Mo à 2 000). Une table attributaire n'affiche aucune coordonnée.
     * ⚠️ MAIS L'ACCROCHAGE DU TRACÉ PASSE PAR LA MÊME ROUTE et lui, il lui
     * FAUT la géométrie : le défaut reste donc `false`. L'inverser rendrait
     * l'accrochage inerte, sans une erreur.
     */
    dansEnveloppe: (b: Enveloppe, limite = 500, sansGeometrie = false) =>
      appeler<CollectionGeo>(`/parcelles?ouest=${b.ouest}&sud=${b.sud}&est=${b.est}&nord=${b.nord}`
        + `&limite=${limite}${sansGeometrie ? '&sansGeometrie=1' : ''}`),
    /** POST /parcelles/:identifiant/activer — inscrit la parcelle au plan cadastral (src/modules/parcelles.ts:208). */
    /** Le sceau se frappe UNE fois — le serveur refuse la seconde (409 nommé). */
    referencer: (identifiant: string) =>
      appeler<{ identifiant: string; reference: string }>(
        `/parcelles/${encodeURIComponent(identifiant)}/referencer`, { method: 'POST' }),
    activer: (identifiant: string, corps: DemandeActivation) =>
      appeler<ResultatActivation>(`/parcelles/${encodeURIComponent(identifiant)}/activer`,
        { method: 'POST', body: JSON.stringify(corps) }),
    /**
     * GET /parcelles/:identifiant/procedures — la chronologie juridique du
     * fonds : état juridique, procédures et leurs neuf phases, fil du journal.
     */
    procedures: (identifiant: string) =>
      appeler<ChronologieParcelle>(`/parcelles/${encodeURIComponent(identifiant)}/procedures`),

    /**
     * POST /parcelles/verifier-emprise — ce que la garde de recouvrement
     * refuserait, DIT AVANT d'écrire.
     *
     * ⚠️⚠️ CETTE ROUTE EXISTAIT DEPUIS DES MOIS ET AUCUN CLIENT NE L'APPELAIT.
     * Mesuré le 23/09/2026 : `grep verifier-emprise frontend/` rendait ZÉRO.
     * Le serveur savait dire à un géomètre que son emprise mord sur sa
     * voisine ; rien dans l'interface ne posait la question, et rien ne savait
     * non plus dessiner l'emprise qu'il aurait fallu lui soumettre. Le refus
     * n'arrivait donc qu'au dépôt, sur une géométrie déjà saisie.
     *
     * ⚠️ `acceptable` VAUT `null`, ET CE N'EST PAS UN OUBLI. Trois états :
     * `true` acceptable, `false` refusée, `null` NON ÉVALUABLE — c'est-à-dire
     * qu'aucune parcelle active ne borde le tracé. Sur un registre qui n'en
     * compte que quatre, « rien ne recouvre » est vrai par absence de point de
     * comparaison, pas par liberté du sol. Le replier sur `true` donnerait un
     * feu vert que personne n'a allumé.
     */
    verifierEmprise: (geometrie: unknown, exclureId?: string) =>
      appeler<VerdictEmprise>('/parcelles/verifier-emprise', {
        method: 'POST',
        body: JSON.stringify({ geometrie, ...(exclureId ? { exclureId } : {}) }),
      }),

    /**
     * POST /parcelles — l'immatriculation, depuis le tracé.
     *
     * ⚠️ LA PARCELLE NAÎT EN « PROJET », PAS EN « ACTIVE ». Tant qu'elle n'est
     * pas inscrite au plan (`POST /parcelles/:id/activer`), elle ne s'oppose à
     * personne et deux prétentions concurrentes peuvent coexister — c'est même
     * l'objet de l'instruction. L'écran doit le DIRE : un agent qui croirait
     * avoir opposé son tracé au monde en le déposant se tromperait d'un cran
     * entier de procédure.
     */
    creer: (corps: DemandeImmatriculation) =>
      appeler<{ parcelle: Record<string, unknown> }>('/parcelles', {
        method: 'POST', body: JSON.stringify(corps),
      }),

    /**
     * PATCH /parcelles/:identifiant/emprise — corriger le contour d'un PROJET.
     *
     * ⚠️⚠️ CE CHEMIN MANQUAIT, ET SON ABSENCE NE SE VOYAIT PAS. L'atelier
     * offrait « Modifier GN-PAR-… », rouvrait la bonne géométrie, et déposait
     * par `POST /parcelles` : une SECONDE parcelle naissait en projet, la
     * première restait intacte, rien ne les rattachait, et aucune erreur
     * n'était rendue. Le seul symptôme était un registre qui grossit.
     *
     * ⚠️ LE SERVEUR REFUSE TOUT CE QUI N'EST PAS UN PROJET, et ce n'est pas une
     * prudence d'écran qu'on pourrait contourner : une limite inscrite au plan
     * se déplace par un levé, et si elle porte des droits, par une mutation de
     * rectification. `domaine/effet-releve.ts` oppose déjà ce refus à un
     * géomètre muni d'un levé.
     */
    modifierEmprise: (identifiant: string, corps: CorrectionEmprise) =>
      appeler<ResultatCorrectionEmprise>(
        `/parcelles/${encodeURIComponent(identifiant)}/emprise`,
        { method: 'PATCH', body: JSON.stringify(corps) }),
    /**
     * PATCH /parcelles/emprises — le LOT SOLIDAIRE (2 à 20 corrections, une
     * transaction). C'est la moitié écran de l'édition topologique partagée
     * (D.968) : deux PATCH unitaires successifs ouvriraient un interstice
     * ENTRE les appels, définitif si le second échoue. L'écran ne boucle
     * JAMAIS sur l'unitaire pour un geste mitoyen — règle du service,
     * parcelles.ts:760-764.
     */
    modifierEmprises: (corps: {
      corrections: Array<{
        identifiant: string; geometrie: unknown;
        origine: DemandeImmatriculation['origine'];
        precisionM?: number; methode?: string; source?: string;
      }>;
      motif: string;
    }) =>
      appeler<{
        corrigees: Array<Record<string, unknown>>;
        /** `null` = les fonds ne se touchent pas — PAS « écart nul ». */
        ecart_max_au_contact_m: number | null;
        chevauchements: number;
      }>('/parcelles/emprises', { method: 'PATCH', body: JSON.stringify(corps) }),
  },
  /**
   * L'EXPORT TABULAIRE — ce que l'atelier retient, sorti en tableur.
   *
   * ⚠️ `compter` AVANT `csv`, ET CE N'EST PAS DU CONFORT. Le service refuse un
   * export sans sélection (2,7 millions de lignes dont la quasi-totalité est
   * un canevas) et tronque à sa limite. Sans le décompte préalable, le bouton
   * ne sait pas s'il va rendre douze lignes ou quarante mille, et l'agent
   * l'apprend en attendant.
   */
  exports: {
    compterParcelles: (sel: SelectionExport) =>
      appeler<{ total: number; limite: number; seraTronque: boolean }>(
        '/exports/parcelles/compter', { method: 'POST', body: JSON.stringify(sel) }),
    parcellesCsv: (sel: SelectionExport) =>
      appelerFichier('/exports/parcelles.csv', { method: 'POST', body: JSON.stringify(sel) }),
  },
  /**
   * D.926 — L'IMPORT DES FICHIERS DE GÉOMÈTRE.
   *
   * ⚠️ DEUX TEMPS, ET LE PREMIER N'ÉCRIT RIEN. `analyser` joue les contrôles
   * dans une transaction annulée : l'agent voit les recouvrements AVANT de
   * déposer quatre cents parcelles qu'il faudrait retirer une à une. Sans ce
   * temps, le seul moyen de savoir ce qu'un fichier contient est de l'avoir
   * déjà déposé.
   */
  imports: {
    analyser: (corps: CorpsImport) =>
      appeler<AnalyseImport>('/imports/sig/analyser', { method: 'POST', body: JSON.stringify(corps) }),
    deposer: (corps: CorpsImport & DepotImport) =>
      appeler<DepotImportFait>('/imports/sig/deposer', { method: 'POST', body: JSON.stringify(corps) }),
    lots: () => appeler<{ lots: LotImport[] }>('/imports/lots'),
  },
  /**
   * D.927 — LE CHEMINEMENT : la parcelle telle que l'acte l'énonce.
   *
   * ⚠️ `derouler` et `ajuster` N'ÉCRIVENT RIEN. Un géomètre saisit les mesures
   * d'un titre ancien et doit VOIR la fermeture avant de déposer.
   */
  cogo: {
    derouler: (c: Cheminement) =>
      appeler<DerouleCogo>('/cogo/derouler', { method: 'POST', body: JSON.stringify(c) }),
    ajuster: (c: Cheminement & { methodeCalcul: MethodeCalcul; fixes?: string[] }) =>
      appeler<AjustementCogo>('/cogo/ajuster', { method: 'POST', body: JSON.stringify(c) }),
    deposer: (c: Cheminement & DepotCogo) =>
      appeler<DepotCogoFait>('/cogo/deposer', { method: 'POST', body: JSON.stringify(c) }),
    fiche: (reference: string) => appeler<FicheCogo>(`/cogo/${encodeURIComponent(reference)}`),
  },
  /**
   * D.930 — LA REMONTÉE DU TERRAIN.
   *
   * ⚠️ `enSouffrance` MONTRE CE QUI N'EST PAS ENTRÉ AU REGISTRE, pas ce qui y
   * est entré. Une file qui n'affiche que ses succès perd des données en
   * silence : personne ne va chercher ce qui manque.
   */
  terrain: {
    synchroniser: (c: LotTerrain) =>
      appeler<RemonteeFaite>('/terrain/synchroniser', { method: 'POST', body: JSON.stringify(c) }),
    enSouffrance: () =>
      appeler<{ enSouffrance: number; operations: OperationEnSouffrance[] }>('/terrain/en-souffrance'),
  },
  /**
   * LES SERVICES OGC — et l'écran ne les CONSOMME pas, il les ADMINISTRE.
   *
   * ⚠️⚠️ CE QUE CES DEUX APPELS SONT, ET CE QU'ILS NE SONT PAS. `/ogc/wfs` et
   * `/ogc/wms` existent pour un SIG TIERS — QGIS, ArcGIS, GeoServer. Bâtir un
   * client WMS dans une application qui porte déjà MapLibre et onze index GIST
   * serait une seconde carte, plus lente, sur les mêmes données. On appelle
   * donc UNE opération, `GetCapabilities`, pour une seule raison : montrer à
   * l'administrateur **le document que le tiers reçoit**, donc les couches que
   * son habilitation lui ouvre réellement.
   *
   * ⚠️ AUCUNE ROUTE JSON DE CONFORT. Une `GET /ogc/couches` taillée pour
   * l'écran divergerait du vrai document au premier changement de catalogue :
   * on croirait avoir fermé une couche qui resterait servie.
   */
  ogc: {
    capacitesWfs: () =>
      appelerFichier('/ogc/wfs?service=WFS&version=2.0.0&request=GetCapabilities'),
    capacitesWms: () =>
      appelerFichier('/ogc/wms?service=WMS&version=1.3.0&request=GetCapabilities'),
  },
  /** §19 — le centre de contrôle des anomalies de cohérence. */
  anomalies: {
    synthese: () => appeler<SyntheseAnomalies>('/anomalies/synthese'),
    liste: (q: URLSearchParams) => appeler<{ etat: string; limite: number; decalage: number;
      tronque: boolean; anomalies: AnomalieFile[] }>(`/anomalies?${q}`),
    /** POST /anomalies/:id/traiter — la décision et son motif, jamais une case cochée. */
    traiter: (id: string, corps: { decision: CodeDecisionAnomalie; motif: string }) =>
      appeler<ResultatTraitement>(`/anomalies/${encodeURIComponent(id)}/traiter`,
        { method: 'POST', body: JSON.stringify(corps) }),
  },
  graphe: {
    parcelle: (identifiant: string) => appeler<GrapheParcelle>(`/graphe/parcelle/${encodeURIComponent(identifiant)}`),
    /** POST /graphe/fiscal — rapproche un objet fiscal d'une parcelle (src/modules/graphe.ts:110). */
    rattacherFiscal: (corps: DemandeRattachementFiscal) =>
      appeler<ResultatRapprochementFiscal>('/graphe/fiscal', { method: 'POST', body: JSON.stringify(corps) }),
  },
  territoire: {
    couverture: () => appeler<Couverture>('/territoire/couverture'),
    divisions: (parent?: string) => appeler<{ divisions: Division[] }>(`/territoire/divisions${parent ? `?parent=${parent}` : ''}`),
    /**
     * GET /territoire/circonscriptions — le découpage ADMINISTRATIF déclaré par
     * l'État, avec ses codes postaux. Mesuré le 24/09/2026 : 8 régions,
     * 38 préfectures, 36 communes, 304 sous-préfectures, et **0 orpheline sous
     * la région** — la cascade est entière, elle peut porter un formulaire.
     *
     * ⚠️ CE N'EST PAS `ter.division`. Celle-ci porte les frontières OBSERVÉES
     * (OpenStreetMap), qui bornent le maillage ; celle-là porte le découpage
     * administratif. Les confondre ferait croire à un accord que personne n'a
     * vérifié.
     */
    circonscriptions: (c: { echelon?: Echelon | 'tous'; q?: string } = {}) => {
      const p = new URLSearchParams();
      if (c.echelon) p.set('echelon', c.echelon);
      if (c.q) p.set('q', c.q);
      return appeler<ReponseCirconscriptions>(`/territoire/circonscriptions?${p}`);
    },
    /** POST /territoire/parcelles/:identifiant/revendication — un occupant se déclare (src/modules/territoire.ts:284). */
    revendiquer: (identifiant: string, corps: DemandeRevendication) =>
      appeler<ResultatRevendication>(`/territoire/parcelles/${encodeURIComponent(identifiant)}/revendication`,
        { method: 'POST', body: JSON.stringify(corps) }),
  },
  demandes: {
    /** GET /demandes/catalogue — ce que MON guichet peut déposer, et pourquoi pas. */
    catalogue: () => appeler<CatalogueDemandes>('/demandes/catalogue'),
    /** GET /demandes/flux — où le travail s'accumule, et ce qui ne peut pas avancer. */
    flux: () => appeler<FluxMetier>('/demandes/flux'),
    /** GET /demandes/file — ce qui attend mon service. Bornée par l'ORGANISATION. */
    file: () => appeler<FileDemandes>('/demandes/file'),
    fiche: (reference: string) => appeler<FicheDemande>(`/demandes/${encodeURIComponent(reference)}`),
    deposer: (corps: {
      type: string; formulaire: unknown; identifiantParcelle?: string;
      /** ⚠️ Jetée par le SERVEUR, dans la transaction du dépôt : un second
       *  appel qui échoue laisserait une saisie fantôme, redéposable. */
      cleBrouillon?: string;
    }) =>
      appeler<ResultatDepotDemande>('/demandes', { method: 'POST', body: JSON.stringify(corps) }),

    /**
     * LE BROUILLON DE SERVICE — ma saisie en cours, qui me suit de poste en
     * poste. Ce n'est PAS une affaire : rien n'est déposé tant que le
     * formulaire n'a pas été validé. Migration 239, item D.966.
     */
    brouillons: () => appeler<ListeBrouillons>('/demandes/brouillons'),
    /** Le corps d'une saisie — séparé de la liste : il porte les portraits. */
    brouillonCorps: (cle: string) => appeler<{
      corps: Record<string, unknown> | null; etape?: number; majLe?: string; message?: string;
    }>(`/demandes/brouillons/${encodeURIComponent(cle)}`),
    enregistrerBrouillon: (cle: string, corps: {
      typeCode: string; corps: Record<string, unknown>; etape: number;
      parcelle?: string | null; resume?: string | null;
    }) => appeler<{ cle: string; enregistreLe: string; message: string }>(
      `/demandes/brouillons/${encodeURIComponent(cle)}`,
      { method: 'PUT', body: JSON.stringify(corps) }),
    jeterBrouillon: (cle: string) => appeler<{ supprime: boolean; message: string }>(
      `/demandes/brouillons/${encodeURIComponent(cle)}`, { method: 'DELETE' }),
    /** POST /demandes/:ref/transmettre — finaliser mon étape, passer la main. */
    transmettre: (reference: string, corps: { motif: string; suite: 'poursuivre' | 'aboutir' | 'rejeter' }) =>
      appeler<{ demande: string; transmiseA: string | null; etat: string; message: string }>(
        `/demandes/${encodeURIComponent(reference)}/transmettre`,
        { method: 'POST', body: JSON.stringify(corps) }),
  },
  /**
   * L'AFFAIRE — le parcours, les gestes, le dispatch.
   *
   * ⚠️ AUCUNE RÈGLE N'EST RECOPIÉE ICI. Le service DIT quels gestes sont
   * ouverts et POURQUOI les autres sont fermés ; l'écran affiche. Deux
   * évaluations de la même règle divergent au premier correctif appliqué à
   * une seule — mesuré dans ce dépôt : 10 gestes sur 24 exigeaient une
   * permission qu'aucun rôle ne détenait, et l'écran les affichait quand même.
   */
  /**
   * VERSEMENT D'IDENTITÉ DEPUIS MOWIBO ID — l'agent demande, la personne consent.
   *
   * ⚠️ `etat()` EXISTE POUR QUE « FERMÉ » NE SE LISE PAS « CASSÉ ». Une
   * instance sans clé API reste un registre complet : l'identité s'établit par
   * carte CEDEAO ou par déclaration. Sans cet appel, l'écran afficherait un
   * bouton qui rend 501 et l'agent ouvrirait un incident devant une variable
   * d'environnement absente.
   */
  versements: {
    etat: () => appeler<EtatVersement>('/versements/etat'),
    deLAffaire: (reference: string) =>
      appeler<{ disponible: boolean; versements: LigneVersement[] }>(
        `/affaires/${encodeURIComponent(reference)}/versements`),
    demander: (reference: string, corps: { titulaireId: string; motif: string; rolePartie?: string }) =>
      appeler<DemandeVersement>(
        `/affaires/${encodeURIComponent(reference)}/versements`,
        { method: 'POST', body: JSON.stringify(corps) }),
    /** L'union des parties PAR RÔLE — témoins compris (D.1479). */
    parties: (reference: string) =>
      appeler<{ parties: PartieAffaire[]; temoins: number;
        rolesDeclarables: Array<{ role: string; libelle: string }> }>(
        `/affaires/${encodeURIComponent(reference)}/parties`),
    /** L'enrôlement au guichet d'une partie SANS compte Mowibo ID (D.1506). */
    /** L'enrôlement DÉCLARE la partie : le rôle toujours, la personne quand on
     *  la connaît — il vise qui n'est encore nulle part (D.1519). */
    enroler: (reference: string,
      partie: { role: string; titulaireId?: string; nomDeclare?: string }) =>
      appeler<{ demandeId: string; lien: string; expireLe: string; aDire: string }>(
        `/affaires/${encodeURIComponent(reference)}/parties/enroler`,
        { method: 'POST', body: JSON.stringify(partie) }),
    /**
     * CHERCHER AVANT DE CRÉER (D.1519). ⚠️ POST, et le NIN dans le CORPS : en
     * chaîne de requête il se déposerait dans les journaux d'accès du proxy,
     * dans le `Referer` des appels suivants et dans l'historique du navigateur
     * du guichet.
     */
    reconnaitre: (reference: string, nin: string) =>
      appeler<Reconnaissance>(
        `/affaires/${encodeURIComponent(reference)}/parties/reconnaitre`,
        { method: 'POST', body: JSON.stringify({ nin }) }),
    /** La fiche existe : on la rattache, on n'en crée pas une seconde (D.1519). */
    rattacher: (reference: string,
      partie: { role: string; titulaireId: string; demanderIdentite?: boolean }) =>
      appeler<{
        rattache: { role: string; titulaireId: string; denomination: string };
        versement: { code: string | null; lien: string; versementId: string } | null;
        versementImpossible: string | null;
        aDire: string;
      }>(
        `/affaires/${encodeURIComponent(reference)}/parties/rattacher`,
        { method: 'POST', body: JSON.stringify(partie) }),
    recevoir: (id: string) =>
      appeler<VersementRecuApi>(`/versements/${encodeURIComponent(id)}/recevoir`,
        { method: 'POST' }),
  },

  affaires: {
    parcours: (reference: string) =>
      appeler<ParcoursAffaire>(`/affaires/${encodeURIComponent(reference)}/parcours`),

    /**
     * Le fil d'instruction. `contenu: null` avec `couvert: true` n'est PAS un
     * message vide : c'est un message qui existe et qui ne vous est pas ouvert.
     * L'écran doit les distinguer, sinon la portée devient indiscernable d'un
     * bogue.
     */
    messages: (reference: string) =>
      appeler<{ affaire: string; messages: MessageAffaire[] }>(
        `/affaires/${encodeURIComponent(reference)}/messages`),
    deposerMessage: (reference: string, corps: {
      portee: 'tous' | 'metier' | 'agent' | 'service';
      porteeCible?: string | null; contenu: string;
    }) => appeler<{ id: string; deposeLe: string }>(
      `/affaires/${encodeURIComponent(reference)}/messages`,
      { method: 'POST', body: JSON.stringify(corps) }),
    chronologie: (reference: string) =>
      appeler<ChronologieAffaire>(`/affaires/${encodeURIComponent(reference)}/chronologie`),
    transition: (reference: string, corps: {
      sens: string; motif: string; detail?: Record<string, unknown>;
      documentId?: string | null; signatureId?: string | null; piecesAttendues?: string[];
    }) => appeler<{ affaire: string; vers: string; versLibelle: string; etat: string;
                    transmission: boolean; message: string }>(
      `/affaires/${encodeURIComponent(reference)}/transition`, { method: 'POST', body: JSON.stringify(corps) }),
    prendre: (reference: string) =>
      appeler<{ message: string }>(`/affaires/${encodeURIComponent(reference)}/prendre`, { method: 'POST', body: '{}' }),
    rendre: (reference: string, motif: string) =>
      appeler<{ candidatsRestants: number; message: string }>(
        `/affaires/${encodeURIComponent(reference)}/rendre`, { method: 'POST', body: JSON.stringify({ motif }) }),
    escalader: (reference: string, motif: string) =>
      appeler<{ vers: string; message: string }>(
        `/affaires/${encodeURIComponent(reference)}/escalader`, { method: 'POST', body: JSON.stringify({ motif }) }),
    deposerPiece: (reference: string, codePiece: string, documentId: string) =>
      appeler<{ piece: string; statut: string }>(
        `/affaires/${encodeURIComponent(reference)}/pieces`, { method: 'POST', body: JSON.stringify({ codePiece, documentId }) }),
    statuerPiece: (reference: string, code: string, decision: 'acceptee' | 'refusee', motif?: string) =>
      appeler<{ piece: string; statut: string }>(
        `/affaires/${encodeURIComponent(reference)}/pieces/${encodeURIComponent(code)}/statuer`,
        { method: 'POST', body: JSON.stringify({ decision, motif }) }),
    accuser: (reference: string) =>
      appeler<{ accusees: number; message: string }>(
        `/affaires/${encodeURIComponent(reference)}/accuser`, { method: 'POST', body: '{}' }),
    monBureau: () => appeler<MonBureau>('/affaires/mon-bureau'),
    file: () => appeler<FileService>('/affaires/file'),
    attribuer: (mode: string, combien = 1) =>
      appeler<{ attribuees: unknown[]; message: string }>('/affaires/attribuer',
        { method: 'POST', body: JSON.stringify({ mode, combien }) }),

    /* ── LE FORMULAIRE DE L'ACTE, ET LE TÉLÉVERSEMENT ────────────────────
     * ⚠️ La définition du formulaire est CALCULÉE par le service depuis
     * `doc.acte_autorite.composition` — la même source que l'acte imprimé.
     * L'écran ne connaît ni la donation, ni la succession, ni le nom d'une
     * seule partie : il dessine sept types de champ. C'est ce qui rend
     * impossible le « formulaire qui a oublié un champ de l'acte », et qui
     * fait d'un type d'acte de plus un INSERT, sans une ligne d'interface.
     * ──────────────────────────────────────────────────────────────────── */
    formulaire: (reference: string) =>
      appeler<FormulaireActe>(`/affaires/${encodeURIComponent(reference)}/formulaire`),
    enregistrerFormulaire: (reference: string, corps: {
      contenu: Record<string, unknown>; motif: string; versionAttendue: number;
    }) => appeler<{ version: number; manquants: number; total: number; complete: number; message: string }>(
      `/affaires/${encodeURIComponent(reference)}/formulaire`,
      { method: 'PUT', body: JSON.stringify(corps) }),
    typesDePiece: (reference: string) =>
      appeler<{ types: { code: string; libelle: string; aide: string | null }[] }>(
        `/affaires/${encodeURIComponent(reference)}/pieces/types`),
    /** POST — le fichier ET la pièce en UN geste, une seule transaction. */
    /**
     * LES SIGNATURES PAR LIEN PUBLIC — §D.964, migration 238.
     *
     * ⚠️ ON NE SIGNE PAS UN FORMULAIRE, ON SIGNE UN ACTE :
     * `doc.signature.document_id` est NOT NULL. Le lien ne s'émet donc
     * qu'une fois l'acte dressé par l'autorité réceptrice.
     */
    signatures: (reference: string) =>
      appeler<EtatSignaturesAffaire>(`/affaires/${encodeURIComponent(reference)}/signatures`),
    /** QUI peut recevoir un lien — et qui ne peut pas, avec la raison. */
    signataires: (reference: string) => appeler<{
      affaire: string;
      signataires: Array<{
        partie: string; nom: string; titulaireId: string | null;
        rapproche: string | null; empechement: string | null;
      }>;
      inscritsSansRole: Array<{ titulaireId: string; nom: string; nature: string }>;
      lecture: string;
    }>(`/affaires/${encodeURIComponent(reference)}/signataires`),
    emettreLienSignature: (reference: string, corps: {
      partie: string; nomAttendu: string; titulaireId: string;
      acte?: string; jours?: number;
    }) => appeler<{
      lien: string; jeton: string; partie: string; nomAttendu: string;
      acte: string; expireDansJours: number; message: string;
    }>(`/affaires/${encodeURIComponent(reference)}/signatures/lien`,
       { method: 'POST', body: JSON.stringify(corps) }),

    televerserPiece: (reference: string, corps: {
      codePiece: string; typeCode?: string; emetteur: string; dateDocument: string;
      typeMime: string; contenuBase64: string; empreinteAnnoncee?: string;
      sensibilite?: number; motif: string;
    }) => appeler<ResultatTeleversement>(
      `/affaires/${encodeURIComponent(reference)}/pieces/televerser`,
      { method: 'POST', body: JSON.stringify(corps) }),

    /* ── Les routes héritées du guichet, fusionnées ici. ──────────────────
     * ⚠️ ELLES ÉTAIENT DANS UN SECOND `affaires: {` — deux clés du même nom
     * dans le même objet littéral, dont la SECONDE écrase la première en
     * silence. Toutes les méthodes du parcours auraient été absentes À
     * L'EXÉCUTION, sans aucun signe au build hors du typage. Motif « un nom
     * pris deux fois », déjà payé dans ce dépôt sur une règle CSS.
     * ──────────────────────────────────────────────────────────────────── */
    /** GET /dossiers — la file du guichet, filtrée par ÉTAPE (pas par « en cours »). */
    liste: (q: { etape?: string; requerant?: string; vivants?: boolean } = {}) => {
      const p = new URLSearchParams();
      if (q.etape) p.set('etape', q.etape);
      if (q.requerant) p.set('requerant', q.requerant);
      p.set('vivants', String(q.vivants ?? true));
      return appeler<{ affaires: DossierLigne[]; total: number; tronque: boolean }>(`/affaires?${p}`);
    },
    /** GET /dossiers/:reference — la vision globale : fil, actes, levés, pièces, titre. */
    fiche: (reference: string) => appeler<FicheAffaire>(`/affaires/${encodeURIComponent(reference)}`),
    etapes: () => appeler<{ etapes: string[] }>('/affaires/reglages/etapes'),
    ouvrir: (corps: { requerantTitulaireId: string; objet: string; identifiantParcelle?: string }) =>
      appeler<ResultatOuverture>('/affaires', { method: 'POST', body: JSON.stringify(corps) }),
    /** POST /dossiers/:reference/recepisse — idempotent : un second appel ne réédite rien. */
    editerRecepisse: (reference: string) =>
      appeler<ResultatRecepisse>(`/affaires/${encodeURIComponent(reference)}/recepisse`,
        { method: 'POST', body: JSON.stringify({}) }),
    clore: (reference: string, corps: { suite: 'abouti' | 'abandonne' | 'rejete'; motif: string }) =>
      appeler<{ affaire: string; suite: string; message: string }>(
        `/affaires/${encodeURIComponent(reference)}/clore`,
        { method: 'POST', body: JSON.stringify(corps) }),
  },
  /**
   * §17 · §50-§53 — LES SUCCESSIONS.
   *
   * ⚠️ CES HUIT ROUTES EXISTAIENT ET N'AVAIENT AUCUN APPELANT. Mesuré le
   * 21/09/2026 : le module `/successions` est complet — vocations en
   * fractions exactes, option des héritiers, masse, projet de partage — et
   * `api.ts` ne le citait nulle part. Le service savait liquider une
   * succession ; aucun agent ne pouvait le lui demander.
   */
  successions: {
    liste: (statut?: string) =>
      appeler<{ successions: any[]; comptes?: Record<string, number>;
                portee?: string; lecture?: string }>(
        `/successions${statut ? `?statut=${encodeURIComponent(statut)}` : ''}`),
    ouvrir: (corps: unknown) => appeler<any>('/successions', { method: 'POST', body: JSON.stringify(corps) }),
    fiche: (reference: string) => appeler<Succession>(`/successions/${encodeURIComponent(reference)}`),
    inscrireAyantDroit: (reference: string, corps: unknown) =>
      appeler<any>(`/successions/${encodeURIComponent(reference)}/ayants-droit`,
        { method: 'POST', body: JSON.stringify(corps) }),
    /**
     * ⚠️ L'OPTION EST LE GESTE LE PLUS LOURD DE L'ÉCRAN : accepter une
     * succession déficitaire fait supporter la dette. Le service rend
     * `liquidation.lecture` qui le dit ; l'écran doit le MONTRER avant le
     * bouton, jamais après.
     */
    opter: (reference: string, id: string, corps: unknown) =>
      appeler<any>(`/successions/${encodeURIComponent(reference)}/ayants-droit/${encodeURIComponent(id)}/option`,
        { method: 'POST', body: JSON.stringify(corps) }),
    fixerVocation: (reference: string, id: string, corps: unknown) =>
      appeler<any>(`/successions/${encodeURIComponent(reference)}/ayants-droit/${encodeURIComponent(id)}/vocation`,
        { method: 'POST', body: JSON.stringify(corps) }),
    inscrireMasse: (reference: string, corps: unknown) =>
      appeler<any>(`/successions/${encodeURIComponent(reference)}/masse`,
        { method: 'POST', body: JSON.stringify(corps) }),
    projetPartage: (reference: string) =>
      appeler<ProjetPartage>(`/successions/${encodeURIComponent(reference)}/projet-partage`),
  },

  mutations: {
    /**
     * GET /mutations — la liste, filtrable par statut ET par type.
     *
     * ⚠️ LE FILTRE EST ENVOYÉ AU SERVICE, JAMAIS APPLIQUÉ ICI. La page est
     * tronquée à 50 : filtrer dans le navigateur rendrait « aucune vente » à
     * un notaire qui en a douze, dès que douze actes plus récents d'un autre
     * type les précèdent. C'est le défaut du filtre après `LIMIT`, que la
     * route commente déjà pour le cloisonnement par mandat.
     */
    liste: (q: { statut?: string; type?: string } = {}) => {
      const p = new URLSearchParams();
      if (q.statut) p.set('statut', q.statut);
      if (q.type) p.set('type', q.type);
      return appeler<ListeMutations>(`/mutations${p.toString() ? `?${p}` : ''}`);
    },
    types: () => appeler<TypesMutation>('/mutations/types'),
    deposer: (corps: DepotMutation) => appeler<{ mutation: Mutation }>('/mutations', { method: 'POST', body: JSON.stringify(corps) }),
    valider: (reference: string, corps: { decision: 'approuve' | 'rejete'; commentaire?: string }) =>
      appeler<ResultatValidation>(`/mutations/${encodeURIComponent(reference)}/valider`, { method: 'POST', body: JSON.stringify(corps) }),

    /**
     * GET /mutations/:reference — le dossier ET ce qui le bloque.
     *
     * ⚠️ C'EST LE SERVICE QUI DIT CE QUI EST OUVERT (`actions`), jamais
     * l'écran. Recopier ici « si statut = validee, cacher Prononcer »
     * créerait une seconde table des mêmes règles, qui divergerait au premier
     * correctif appliqué à une seule.
     */
    affaire: (reference: string) =>
      appeler<Dossier>(`/mutations/${encodeURIComponent(reference)}`),

    /**
     * GET /mutations/:reference/projection — AVANT · OPÉRATION · APRÈS (§62-§64).
     *
     * ⚠️ ELLE COÛTE PLUSIEURS SECONDES, ET C'EST STRUCTUREL : le service
     * APPLIQUE réellement l'effet dans une transaction qu'il annule — seule
     * façon d'obtenir une projection qui ne puisse pas diverger de
     * l'application réelle. L'écran ne la demande donc que pour un acte qui
     * n'a pas encore produit ses effets, et une seule fois.
     */
    projection: (reference: string) =>
      appeler<Projection>(`/mutations/${encodeURIComponent(reference)}/projection`),

    /** POST /mutations/:reference/publicite — §31, ouvre le délai d'opposition. */
    ouvrirPublicite: (reference: string, corps: { support: string; delaiJours?: number }) =>
      appeler<{ mutation: string; echeance: string; message: string }>(
        `/mutations/${encodeURIComponent(reference)}/publicite`,
        { method: 'POST', body: JSON.stringify(corps) }),

    /** POST /mutations/:reference/opposition — §31, s'opposer pendant le délai. */
    inscrireOpposition: (reference: string, corps: { objet: string; opposantNom: string }) =>
      appeler<{ opposition: string; message: string }>(
        `/mutations/${encodeURIComponent(reference)}/opposition`,
        { method: 'POST', body: JSON.stringify(corps) }),

    /** POST /mutations/:reference/immatriculer — PRONONCER. Un acte, pas un avis. */
    immatriculer: (reference: string, motif: string) =>
      appeler<{ mutation: string; titre: { identifiant: string } | null; message: string }>(
        `/mutations/${encodeURIComponent(reference)}/immatriculer`,
        { method: 'POST', body: JSON.stringify({ motif }) }),

    /**
     * POST /mutations/:reference/regulariser-titre — délivrer le titre d'une
     * immatriculation déjà prononcée qui n'en a jamais produit.
     *
     * ⚠️ PAS UN DOUBLON D'`immatriculer` : celle-ci ne prononce rien, elle
     * répare. Deux immatriculations de production (09/09 et 11/09) ont été
     * appliquées avant que l'émission de titre n'existe ; la garde qui l'interdit
     * désormais ne joue qu'au changement et ne les relira jamais.
     */
    regulariserTitre: (reference: string, motif: string) =>
      appeler<{
        mutation: string; titre: { identifiant: string; reference: string };
        droitsRattaches: number; message: string;
      }>(`/mutations/${encodeURIComponent(reference)}/regulariser-titre`,
        { method: 'POST', body: JSON.stringify({ motif }) }),

    /**
     * §7 — LA FICHE D'IDENTIFICATION DES PARTIES.
     *
     * ⚠️ ELLE N'AFFIRME JAMAIS UN RAPPROCHEMENT — elle le propose en disant
     * COMMENT. `rattachePar: 'nom'` veut dire qu'un nom, et rien d'autre, relie
     * la partie déclarée au guichet au titulaire du registre. L'écran doit
     * imprimer cette mention EN FACE du nom, jamais en note de bas de page.
     */
    ficheParties: {
      url: (reference: string) =>
        `/mutations/${encodeURIComponent(reference)}/fiche-parties`,
      etat: (reference: string) =>
        appeler<EtatFicheParties>(`/mutations/${encodeURIComponent(reference)}/fiche-parties/etat`),
      editions: (reference: string) =>
        appeler<EditionsFicheParties>(`/mutations/${encodeURIComponent(reference)}/fiche-parties/editions`),
    },

    /**
     * §10 — LE DOSSIER DE PIÈCES DE L'ACTE : les huit pièces, l'état de chacune.
     *
     * ⚠️ PAS D'`url()`, ET CE N'EST PAS UN OUBLI. Toutes les autres pièces en
     * ont une parce qu'elles se DÉLIVRENT ; celle-ci ne délivre rien, elle
     * constate. Lui poser une URL de délivrance promettrait un document qui
     * n'existe pas — le bouton se trouve sur chaque pièce, pas sur la liste.
     */
    pieces: (reference: string) =>
      appeler<DossierDePieces>(`/mutations/${encodeURIComponent(reference)}/pieces`),
  },
  releves: {
    liste: (statut?: string) => appeler<{ releves: Releve[] }>(`/releves${statut ? `?statut=${statut}` : ''}`),
    fiche: (reference: string) => appeler<FicheReleve>(`/releves/${encodeURIComponent(reference)}`),
    deposer: (corps: DepotReleve) => appeler<{ releve: Releve; qualite: Qualite }>('/releves', { method: 'POST', body: JSON.stringify(corps) }),
    controler: (reference: string) => appeler<{ releve: string; qualite: Qualite }>(`/releves/${encodeURIComponent(reference)}/controler`, { method: 'POST' }),
    /** §adjudication — qui doit être entendu, et qui l'a été. */
    riverains: (reference: string) =>
      appeler<Adjudication>(`/releves/${encodeURIComponent(reference)}/riverains`),
    recueillir: (reference: string, corps: RecueilRiverain) =>
      appeler<{ riverain: { id: string; cote: string; verdict: string }; titulaire: string; identifie: boolean; avertissement: string | null }>(
        `/releves/${encodeURIComponent(reference)}/riverains`,
        { method: 'POST', body: JSON.stringify(corps) }),
    decider: (reference: string, corps: { decision: 'valide' | 'rejete'; motif: string }) =>
      appeler<ResultatDecision>(`/releves/${encodeURIComponent(reference)}/decider`, { method: 'POST', body: JSON.stringify(corps) }),

    /* ── Le LOT DE LEVÉS SOLIDAIRE (D.1489) : le géomètre PROPOSE un geste
       mitoyen (2-20 parcelles), l'État le décide EN BLOC — l'application des
       N emprises et le témoin du sol continu partagent une transaction. */
    deposerLot: (corps: DepotLotLeves) =>
      appeler<ResultatDepotLot>('/releves/lot', { method: 'POST', body: JSON.stringify(corps) }),
    lot: (reference: string) =>
      appeler<FicheLotLeves>(`/releves/lot/${encodeURIComponent(reference)}`),
    deciderLot: (reference: string, corps: { decision: 'valide' | 'rejete'; motif: string }) =>
      appeler<ResultatDecisionLot>(`/releves/lot/${encodeURIComponent(reference)}/decider`,
        { method: 'POST', body: JSON.stringify(corps) }),

    /**
     * LES BORNES DU LEVÉ — les points mesurés, et ce qui est inscrit sur chacun.
     *
     * ⚠️ `cad.borne` était lue par quatre documents et écrite par RIEN : 0 ligne
     * pour 988 695 parcelles, donc aucun plan de bornage délivrable dans le pays
     * alors que son générateur existe. Ces deux routes sont le geste manquant.
     */
    bornes: {
      lire: (reference: string) =>
        appeler<BornesDuLeve>(`/releves/${encodeURIComponent(reference)}/bornes`),
      /** ⚠️ La position vient du POINT MESURÉ (`indicePoint`), jamais d'une saisie. */
      inscrire: (reference: string, corps: DemandeInscriptionBornes) =>
        appeler<ResultatInscriptionBornes>(`/releves/${encodeURIComponent(reference)}/bornes`,
          { method: 'POST', body: JSON.stringify(corps) }),
    },

    /**
     * §6 — LE PROCÈS-VERBAL DE BORNAGE CONTRADICTOIRE.
     *
     * ⚠️ PAS D'APERÇU À L'ÉCRAN, ET CE N'EST PAS UN OUBLI. Le plan et la
     * situation rendent du SVG sous `?apercu=1` parce qu'ils DESSINENT ; le PV
     * ne dessine pas, il constate — et ce qu'il constate est lisible sans lui
     * par `etat()`, qui rend le verdict AVANT toute édition. Poser un faux
     * aperçu ici servirait un PDF dans un cadre annoncé comme une image.
     */
    procesVerbal: {
      /** Le PDF délivré. ⚠️ Écrit au coffre et versionné : ce n'est pas un rendu jetable. */
      url: (reference: string) =>
        `/releves/${encodeURIComponent(reference)}/proces-verbal`,
      /** Ce que le PV DIRA, sans l'éditer — le verdict avant la promesse. */
      etat: (reference: string) =>
        appeler<EtatPvBornage>(`/releves/${encodeURIComponent(reference)}/proces-verbal/etat`),
      /** §11 — les versions déjà délivrées, et si leur contradictoire tient encore. */
      editions: (reference: string) =>
        appeler<EditionsPvBornage>(`/releves/${encodeURIComponent(reference)}/proces-verbal/editions`),
    },
  },
  titulaires: {
    /**
     * POST /titulaires — inscrire une personne qui possède DÉJÀ.
     * ⚠️ Inscrire n'est pas attribuer : aucun droit n'est créé ici.
     */
    inscrire: (corps: {
      type: string; denomination: string; motif: string;
      nin?: string; ninSource?: string; mowiboMid?: string; referenceExterne?: string;
    }) => appeler<ResultatInscription>('/titulaires', { method: 'POST', body: JSON.stringify(corps) }),

    /** POST /titulaires/:id/nin — rattacher un identifiant national. */
    rattacherNin: (id: string, corps: { nin: string; source: string; motif: string }) =>
      appeler<{ titulaire: string; nin_source: string; nin_verifie_le: string | null; message?: string }>(
        `/titulaires/${encodeURIComponent(id)}/nin`, { method: 'POST', body: JSON.stringify(corps) }),

    /** POST /titulaires/:id/contribuable — rapprocher du fisc, par empreinte. */
    rapprocherFisc: (id: string, motif?: string) =>
      appeler<{ statut: string; message: string }>(
        `/titulaires/${encodeURIComponent(id)}/contribuable`,
        { method: 'POST', body: JSON.stringify(motif ? { motif } : {}) }),

    /**
     * ⚠️⚠️ `titulaires.chercher` A ÉTÉ RETIRÉE LE 21/09/2026 — UN NOM, UNE CHOSE.
     * Elle appelait la MÊME route que `foncier.chercherTitulaires`
     * (`GET /foncier/titulaires?q=`) sous un second nom, avec un type de retour
     * PLUS PAUVRE : trois champs au lieu de sept, sans `droits_actifs` ni
     * `nin_rattache` ni `proximite`. Mesuré : zéro appelant dans tout le dépôt,
     * contre deux pour l'autre — et c'est pourtant celle-ci que la passation du
     * 21/09 désignait pour chercher un défunt. Un écran écrit contre elle
     * n'aurait pas pu afficher combien de biens la personne détient : sur une
     * succession, c'est exactement le chiffre qui dit si l'on a trouvé le bon
     * homonyme. Deux noms pour une chose, c'est le plus pauvre des deux que la
     * prochaine session emploie.
     */
  },
  oppositions: {
    /** GET /foncier/oppositions — ce qui bloque des immatriculations. */
    liste: (ouvertesSeules = true) =>
      appeler<{ oppositions: Opposition[]; total: number }>(
        `/foncier/oppositions?ouvertes=${ouvertesSeules}`),

    /** POST /foncier/oppositions/:reference/clore — trancher, dans un sens ou l'autre. */
    clore: (reference: string, corps: { suite: 'retenue' | 'ecartee'; motif: string }) =>
      appeler<{ opposition: string; suite: string; message: string }>(
        `/foncier/oppositions/${encodeURIComponent(reference)}/clore`,
        { method: 'POST', body: JSON.stringify(corps) }),
  },
  /**
   * §43 — LES SIGNATURES D'UN ACTE, ET SON SCEAU.
   *
   * ⚠️ CES ROUTES EXISTAIENT SANS AUCUN APPELANT (migration 160). Le service
   * sait signer, révoquer et sceller ; `api.ts` ne les citait nulle part. Un
   * acte ne pouvait être signé que par appel direct — c'est-à-dire par un
   * ingénieur, pas par une étude.
   *
   * ⚠️⚠️ ET LE CORRECTIF ANNONCÉ CI-DESSUS N'EN COUVRAIT QU'UNE SUR TROIS.
   * Ce bloc disait « ces trois routes » au passé, et n'exposait que `etat` —
   * la seule qui ne fait rien. `signer` et `sceller` sont restées
   * inatteignables, et la mesure du 23/09/2026 le montre : **1 scellement pour
   * 81 actes délivrés que la loi veut scellés**, dont 14 étaient signés et
   * scellables le jour même. Un commentaire qui déclare une correction faite
   * est plus coûteux qu'un défaut nu : il fait cesser de chercher.
   */
  signatures: {
    etat: (identifiant: string) =>
      appeler<EtatSignatures>(`/signatures/${encodeURIComponent(identifiant)}`),

    /**
     * ⚠️ `empreinteAttendue` N'EST PAS DÉCORATIVE : elle prouve que le
     * signataire savait CE QU'IL SIGNAIT. Si l'acte a changé entre l'affichage
     * et la signature, le service le détecte AVANT d'inscrire.
     */
    signer: (identifiant: string, corps: {
      role: 'partie' | 'temoin' | 'notaire' | 'autorite' | 'interprete';
      procede: 'electronique_mowibo_id' | 'manuscrite_scannee' | 'empreinte_digitale'
             | 'marque_a_la_croix' | 'cachet_autorite';
      empreinteAttendue: string;
      qualite?: string; signataireTitulaireId?: string; signataireCompteId?: string;
      certificatKid?: string; valeur?: string; preuveDocumentIdentifiant?: string; lieu?: string;
    }) => appeler<{ document: string; signature: Record<string, unknown>; etat: Record<string, unknown> }>(
      `/signatures/${encodeURIComponent(identifiant)}`,
      { method: 'POST', body: JSON.stringify(corps) }),

    /**
     * ⚠️ LE SCEAU ATTESTE UNE CÉRÉMONIE. Le service refuse de sceller un acte
     * que personne n'a signé, et refuse si une signature porte sur une version
     * antérieure : ces deux refus sont le sens même du sceau, pas des
     * obstacles. `mention` dit au nom de quoi l'autorité scelle.
     */
    sceller: (identifiant: string, corps: { mention: string; certificatKid?: string; valeur?: string }) =>
      appeler<{ document: string; autorite: string; scellement: Record<string, unknown>;
                etat: Record<string, unknown> }>(
        `/signatures/${encodeURIComponent(identifiant)}/sceller`,
        { method: 'POST', body: JSON.stringify(corps) }),
  },

  documents: {
    liste: (q: { recherche?: string; typeCode?: string; statut?: string } = {}) => {
      const p = new URLSearchParams();
      for (const [k, v] of Object.entries(q)) if (v) p.set(k, v);
      return appeler<ListeDocuments>(`/documents${p.toString() ? `?${p}` : ''}`);
    },
    fiche: (identifiant: string) => appeler<FicheDocument>(`/documents/${encodeURIComponent(identifiant)}`),
    deposer: (corps: DepotDocument) => appeler<ResultatDepot>('/documents', { method: 'POST', body: JSON.stringify(corps) }),
    revoquer: (identifiant: string, motif: string) =>
      appeler<{ document: string; statut: string; message: string }>(
        `/documents/${encodeURIComponent(identifiant)}/revoquer`, { method: 'POST', body: JSON.stringify({ motif }) }),
    emettreJeton: (identifiant: string, joursValidite?: number) =>
      appeler<{ jeton: string; urlVerification: string; note: string }>(
        `/documents/${encodeURIComponent(identifiant)}/jeton`,
        { method: 'POST', body: JSON.stringify(joursValidite ? { joursValidite } : {}) }),
    /**
     * POST /documents/certificat/:identifiantTitre — édite le titre EN PDF.
     * Le rendu passe par `doctrust-render` ; la preuve (coffre, empreinte,
     * jeton) reste au registre.
     */
    editerCertificat: (identifiantTitre: string, corps: { joursValiditeCode?: number; motif?: string } = {}) =>
      appeler<ResultatCertificat>(
        `/documents/certificat/${encodeURIComponent(identifiantTitre)}`,
        { method: 'POST', body: JSON.stringify(corps) }),

    /**
     * POST /documents/acte/:reference — le certificat d'un ACTE, par son
     * autorité. Une donation est certifiée par la commune, pas par la
     * Conservation : le service le vérifie, l'écran ne le décide pas.
     */
    editerCertificatActe: (reference: string, corps: { joursValiditeCode?: number; motif?: string } = {}) =>
      appeler<ResultatCertificatActe>(
        `/documents/acte/${encodeURIComponent(reference)}`,
        { method: 'POST', body: JSON.stringify(corps) }),

    /** L'URL du fichier : le navigateur la suit avec le cookie de session. */
    urlContenu: (identifiant: string) => `/documents/${encodeURIComponent(identifiant)}/contenu`,
  },
  verify: verifications,
  reglagesTuiles: () => appeler<ReglagesTuiles>('/tuiles/reglages'),

  /**
   * GET /tuiles/version — la version du plan, et le gabarit d'URL qui la porte.
   *
   * ⚠️⚠️ C'EST CE QUI FAIT QUE LA CARTE SUIT L'ÉDITION. Les tuiles étaient
   * servies sous `/tuiles/{z}/{x}/{y}.pbf` en `max-age=300` : après un dépôt,
   * le navigateur resservait sa tuile cinq minutes et l'édition avait l'air de
   * n'avoir rien fait — 200, tuile parfaitement valide, simplement vieille.
   * La version dans l'URL change à chaque écriture (migration 231), donc
   * l'URL change, donc la tuile est redemandée. On ne raccourcit pas le cache :
   * on le rend juste.
   *
   * ⚠️ LE GABARIT EST SERVI, PAS RECOMPOSÉ ICI. Deux endroits qui construisent
   * le même chemin divergent au premier changement appliqué à un seul.
   */
  versionDuPlan: () => appeler<{ version: number; gabarit: string }>('/tuiles/version'),
};

// ── Formes rendues par le service ───────────────────────────────────────────
export interface SuiviEnrolement {
  statut: 'envoyee' | 'ouverte' | 'deposee' | 'close' | 'expiree' | 'annulee';
  decision: string | null;
  motifRejet: string | null;
  /** Constats restitués selon la portée CONSENTIE — pré-rempli, jamais vérité. */
  champs: Record<string, unknown>;
  champsNonConstates: string[];
  sujetNom: string;
}

/**
 * CE QUE LE REGISTRE SAIT D'UN NIN — quatre issues, tranchées PAR LE SERVEUR.
 * L'écran n'en récite aucune règle : il affiche `aDire` et le bouton qui va
 * avec. Deux rédactions de la même règle divergeraient au premier correctif.
 */
export type VerdictReconnaissance =
  | 'deja_partie' | 'rattache_existant' | 'rattache_puis_enrole' | 'inconnu_a_enroler';

export interface Reconnaissance {
  verdict: VerdictReconnaissance;
  titulaire: { id: string; denomination: string; type: string } | null;
  /** `NIN •••••••••••1234 · carte CEDEAO · VÉRIFIÉ` — jamais le numéro. */
  etatNin: string | null;
  identiteVerifiee: boolean;
  aUnCompteMowiboId: boolean;
  rolePartie: string | null;
  aDire: string;
}

export interface PartieAffaire {
  role: string;
  libelle: string;
  source: 'affaire' | 'composition' | 'attestation' | 'succession' | 'guichet';
  titulaire: { id: string; denomination: string } | null;
  nomDeclare: string | null;
  versement: { id: string; statut: string; verseLe: string | null } | null;
  piecesAuDossier: number;
  /** L'enrôlement VIVANT de cette partie (mémoire 272) + son suivi Mowibo ID. */
  enrolement: {
    /** Nuls pour un RATTACHEMENT : il n'y a aucune capture à demander (274). */
    demandeId: string | null; lien: string | null; expireLe: string | null;
    creeLe: string;
    suivi: SuiviEnrolement | null;
    /** Nommé quand Mowibo ID n'a pas répondu — jamais un vide. */
    suiviIndisponible: string | null;
    /**
     * ⚠️ UN RATTACHEMENT N'EST PAS UN ENRÔLEMENT EN PANNE. Sans ce drapeau,
     * l'écran lirait « ni lien, ni suivi » et afficherait l'attente d'une
     * capture qui n'aura jamais lieu.
     */
    rattachement: boolean;
  } | null;
}

export interface EtatVersement {
  disponible: boolean;
  pourquoi: string | null;
  canaux: string[];
}

export interface LigneVersement {
  id: string;
  titulaire_id: string;
  denomination: string;
  statut: string;
  canal: string;
  demande_le: string;
  verse_le: string | null;
  pieces: number;
}

export interface DemandeVersement {
  id: string;
  /** Six chiffres à DICTER. ⚠️ Peut être absent — à traiter, jamais à supposer. */
  code: string | null;
  lien: string;
  qr: string | null;
  expireLe: string;
  titulaire: { id: string; denomination: string };
  aDire: string;
}

export interface VersementRecuApi {
  versementId: string;
  sujet: string;
  denomination: string;
  ninRattache: boolean;
  piecesVersees: string[];
  piecesIllisibles: { cle: string; motif: string }[];
  avertissement: string | null;
}

export interface Enveloppe { ouest: number; sud: number; est: number; nord: number }
export interface Sante { status: string; base: string; territoire_en_base: string | null; initialise: boolean }
export interface Profil {
  compteId: string; nomAffichage: string | null; organisation: string | null; territoire: string | null;
  roles: string[]; porteeNationale: boolean; porteesDivisionIds: string[]; niveauVerification: number;
}
export interface Permission { ressource: string; operation: string; sensibilite_max: number }
export interface Session { ouverte: boolean; profil?: Profil; permissions?: Permission[]; expireLe?: number }
export interface CollectionGeo { type: 'FeatureCollection'; features: any[]; tronque?: boolean; limite?: number }
/** Ce que le registre sait d'une parcelle, prêt à poser dans le formulaire. */
export interface PreRemplissageDemande {
  parcelle: { identifiant: string; statut: string; surfaceM2: number | null };
  immeuble: {
    region: string | null; prefecture: string | null; communeOuSousPrefecture: string | null;
    quartierOuDistrict: string | null; identifiantParcelle: string;
    referenceCadastrale: string | null; lieuDit: string | null; superficieDeclareeM2: number | null;
  };
  /** Toutes les références par lesquelles un agent désigne ce fonds. */
  cadastre: {
    identifiant: string; section: string | null; numeroDansLaSection: string | null;
    circonscription: string | null; circonscriptionCode: string | null;
    /** L'identifiant attendu par `/sections/:code/planche?collectivite=…`. */
    circonscriptionId: string | null;
    division: string | null; divisionCode: string | null;
    referenceHeritee: string | null;
  };
  titulairesInscrits: Array<{
    id: string; type: string; denomination: string;
    nature: string | null; quotite: string; titre: string | null;
  }>;
  origine: { mode: string | null; referenceInscription?: string; pourquoi?: string };
  /** ⚠️ Ce qui pèse AVANT d'ouvrir — une affaire vivante interdit la suivante. */
  cePesantDeja: {
    affaireVivante: string | null; titres: number;
    restrictions: number; mutationsEnInstance: number;
  };
}

export interface FicheParcelle { parcelle: Record<string, any>; [k: string]: unknown }

/** Un recouvrement constaté entre l'emprise soumise et une parcelle active. */
export interface ConflitEmprise {
  identifiant?: string;
  numero_cadastral?: string;
  surface_recouvrement_m2: number;
  part_pct: number;
  [k: string]: unknown;
}

/**
 * Ce que `POST /parcelles` exige. Recopié du `creationSchema` du serveur —
 * ⚠️ `origine` est un enum FERMÉ : une valeur inventée ici rendrait 400 sur un
 * tracé parfaitement bon, et le refus nommerait le champ sans dire lequel des
 * deux côtés a tort.
 */
export interface DemandeImmatriculation {
  geometrie: unknown;
  origine: 'leve_terrain' | 'numerisation_plan' | 'derive_bati' | 'declaration' | 'import';
  motif: string;
  divisionId?: string;
  numeroCadastral?: string;
  precisionM?: number;
  methode?: string;
  source?: string;
}

/**
 * Ce que `PATCH /parcelles/:identifiant/emprise` exige.
 *
 * ⚠️ `divisionId` ET `numeroCadastral` SONT RETIRÉS EXPRÈS. Une correction
 * déplace un contour ; elle ne rattache pas la parcelle à une autre division et
 * ne la renumérote pas. Le serveur ne les lit pas — et zod SUPPRIME les clés
 * qu'il ne connaît pas, donc les envoyer serait ignoré EN SILENCE. Les ôter du
 * type fait dire au compilateur ce que la réponse ne dirait jamais.
 */
export type CorrectionEmprise = Omit<DemandeImmatriculation, 'divisionId' | 'numeroCadastral'>;

export interface ResultatCorrectionEmprise {
  parcelle: string;
  statut: string;
  version: number;
  surface_m2: number;
  perimetre_m: number;
  origine: string;
  surface_avant_m2: number;
  /** `null` si la surface d'avant était nulle — jamais replié sur 0. */
  ecart_surface_pct: number | null;
}

/**
 * Les trois façons de sélectionner un export — `selection` dans
 * `src/modules/exports.ts`.
 *
 * ⚠️ `statut` EST L'ÉNUMÉRATION DE LA BASE, PAS UN VOCABULAIRE D'ÉCRAN. Elle
 * portait « projetee » côté service, un mot que `cad.statut_parcelle` ne
 * connaît pas : l'export rendait alors 200 et un fichier VIDE sur le statut
 * qui porte 99,99 % du parc. Garde : `tests/correction-emprise.test.ts`.
 */
export interface SelectionExport {
  identifiants?: string[];
  divisionCode?: string;
  /** [ouest, sud, est, nord], en degrés. */
  emprise?: [number, number, number, number];
  statut?: 'projet' | 'en_instruction' | 'active' | 'suspendue' | 'retiree';
  limite?: number;
  nom?: string;
}

export interface VerdictEmprise {
  /** `null` = non évaluable (aucune parcelle active à comparer). Jamais replié sur `true`. */
  acceptable: boolean | null;
  evaluable: boolean;
  /** Le témoin de population : ce que la garde a REGARDÉ, pas ce qu'elle a trouvé. */
  voisines_examinees: number;
  parcelles_actives_territoire: number;
  tolerance: { m2: number; pct: number } | null;
  conflits: ConflitEmprise[];
  bloquants: ConflitEmprise[];
}
export interface GrapheParcelle { [k: string]: unknown }
export interface Couverture { [k: string]: unknown }
export interface Division { id: string; code: string; nom: string; niveau: number }

/** Les échelons que `ter.circonscription` connaît — le `z.enum` de la route. */
export type Echelon = 'region' | 'prefecture' | 'commune' | 'sous_prefecture';
/** Une saisie de guichet inachevée — voir `fon.brouillon_vivant`. */
export interface BrouillonGuichet {
  cle: string; type_code: string; type_libelle: string | null;
  parcelle: string | null; etape: number; resume: string | null;
  champs_remplis: number; cree_le: string; maj_le: string; expire_le: string;
}
export interface ListeBrouillons {
  brouillons: BrouillonGuichet[];
  /** ⚠️ Rendue par le service : « aucune saisie » est un FAIT, pas un vide. */
  lecture: string;
}

/** Un lien de signature, tel que le service le rend. */
export interface LienSignature {
  partie: string; nom_attendu: string; acte: string;
  emis_le: string; expire_le: string; consultations: number;
  consomme_le: string | null; revoque_le: string | null; motif_revocation: string | null;
  etat: string;
  /** ⚠️ Rendu SEULEMENT si le lien est vivant ET que l'appelant peut en émettre. */
  jeton: string | null;
}
export interface EtatSignaturesAffaire {
  affaire: string; liens: LienSignature[]; peutEmettre: boolean;
  /** ⚠️ L'adresse publique vient du SERVICE : `window.location` porterait
   *  celle par laquelle l'agent est entré, inatteignable depuis un téléphone. */
  base: string | null;
  lecture: string;
}

export interface Circonscription {
  source_id: number; parent_source_id: number | null; echelon: Echelon;
  nom: string; nom_usuel: string | null; code: string | null; code_postal: string | null;
  population: number | null; superficie_km2: number | null;
  latitude?: number | null; longitude?: number | null;
}
export interface ReponseCirconscriptions {
  criteres: { echelon: string; q?: string };
  effectif: number; par_echelon: Record<string, number>; avec_code_postal: number;
  circonscriptions: Circonscription[];
  /** ⚠️ Rendue par le service : une case vide de code postal est un FAIT. */
  lecture_codes_postaux: string;
}
export interface Mutation { reference: string; type: string; statut: string; parcelle?: string; cree_le?: string; approbations?: number }
export interface DepotMutation {
  type: string;
  identifiantParcelle: string;
  /** ⚠️ TYPÉ, plus `Record<string, unknown>` : c'est ce que la validation
   *  ÉCRIRA au registre foncier, pas un sac d'options. Le type large laissait
   *  passer `{}` — ce que le formulaire envoyait systématiquement. */
  effetProjete: EffetProjete | EffetRectificationProjete;
  motif: string;
  /**
   * ⚠️ LE CHAMP MANQUAIT, ET C'EST LUI QUI ROMPAIT LA CHAÎNE. `POST /mutations`
   * l'accepte depuis la migration 053 ; aucun écran ne le remplissait. Mesuré
   * le 13/09/2026 : 13 mutations sur 13 avec `affaire_id` nul, et les 2
   * dossiers du registre portant 0 acte. Le guichet ouvrait un dossier, l'acte
   * se déposait à côté, et rien ne les reliait — la procédure existait en deux
   * moitiés qui ne se joignaient pas.
   *
   * Facultatif, et il doit le rester : un acte peut légitimement se déposer
   * hors guichet (les 13 antérieurs), et l'imposer d'un coup casserait tout
   * dépôt en cours de route.
   */
  dossierReference?: string;
}
export interface ResultatValidation { mutation: string; statut: string; approbations: number; message?: string }
export interface ReglagesTuiles {
  zoomMinimal: { divisions: number; parcelles: number; batiments: number };
  zoomMaximalDivisions: number;
  /** Zoom d'apparition des noms de rue. Servi, jamais recopié côté écran. */
  zoomNomsDeRue?: number;
  /** Sous ce zoom, la couche bâtiment ne sert que le bâti RATTACHÉ (7,4 %). */
  zoomBatiComplet?: number;
  rangRuesParZoom?: Record<string, number>;
  /**
   * Où le registre a de la matière. `null` = nulle part — et c'est une
   * information, pas une absence de réponse : la carte le DIT plutôt que de
   * s'ouvrir sur du vide en laissant croire à une couche cassée.
   */
  vueConseillee?: {
    centre: [number, number]; zoom: number;
    division: string | null; parcelles: number; motif: string;
  } | null;
  /**
   * ⚠️ DEUX COMPTES, ET LES CONFONDRE ETAIT LE DEFAUT (D.779). `parcellesAuPlan`
   * est un `valide_au IS NULL` — il inclut 1 761 150 mailles en statut
   * 'projet'. `parcellesOpposables` est le `statut = 'active'` : 4 au
   * 11/10/2026. Un facteur 440 000.
   * ⚠️ `parcellesActives` est CONSERVE et deprecie : il portait le sens « au
   * plan » sous le nom « actives ».
   */
  couverture?: {
    parcellesAuPlan: number;
    parcellesOpposables: number;
    /** @deprecated D.779 — employer parcellesAuPlan ou parcellesOpposables. */
    parcellesActives: number;
    divisionsPeuplees: number;
  };
}
export interface MoyensConnexion {
  oidc: { disponible: boolean; detail: string; clientId: string };
  jetonService: { disponible: boolean; detail: string };
  sessionDureeH: number;
}

export interface CompteAdmin {
  id: string; nom_affichage: string | null; actif: boolean; cree_le: string;
  organisation: string | null; roles: string[]; portee_nationale: boolean;
  sessions_vivantes: number;
  /** Le ressort de son organisation — ce que l'écran propose comme portée. */
  organisation_type: string | null;
  ressort_division_id: string | null;
  ressort_division: string | null;
}

export interface InvitationAdmin {
  id: string; nom_affichage: string; roles: string[]; motif: string;
  creee_le: string; expire_le: string; consommee_le: string | null;
  creee_par: string | null; utilisable: boolean;
}

/**
 * ⚠️ LE CONTRAT SUIT LE SERVEUR, IL NE LE PRÉCÈDE PAS. Ce type portait cinq
 * champs quand la route en acceptait cinq. Il en porte dix-sept depuis la
 * migration 087, parce qu'un agent d'État se saisit comme une personne — et
 * chacun a UNE destination en base, jamais deux : la personne dans
 * `fon.titulaire`, la pièce dans `fon.identifiant_personne`, la nomination sur
 * le compte.
 */
export interface DemandeInvitation {
  nomAffichage: string; roles: string[];
  /** ⚠️ Plus facultative depuis la 117 : l'omission accordait le pays entier. */
  portee: 'nationale' | 'division'; porteeDivisionId?: string;
  validiteJours?: number; motif: string;
  organisationId?: string;
  /** Identité civile, déclarée par l'administration qui nomme. */
  nom?: string; prenoms?: string;
  pieceType?: string;   // une valeur de PIECES_IDENTITE (src/partage/pieces-identite.ts)
  pieceNumero?: string; pieceAutorite?: string;
  /** Nomination — c'est elle qui rend l'habilitation opposable. */
  fonction?: string; matricule?: string; telephoneService?: string;
  courrielService?: string; acteNomination?: string; nommeLe?: string;
}

export interface InvitationEmise {
  invitation: string; code: string; expireLe: string; consigne: string;
}

export interface DroitAffiche {
  id: string; nature: string; quotite: string;
  quotite_num: number; quotite_den: number;
  rang: number | null; statut: string;
  valide_du: string; valide_au: string | null; mutation: string | null;
  titulaire: { id: string; denomination: string; type: string;
               identite_verifiee: boolean; nin_rattache: boolean }
           | { masque: true; motif: string };
}

export interface Indivision {
  num: number; den: number; complet: boolean; excedentaire: boolean;
  reste: { num: number; den: number } | null; parts: number; lecture: string;
}

export interface SituationFonciere {
  parcelle: { identifiant: string; statut: string; origine: string; surface_m2: string };
  droits: { actifs: DroitAffiche[]; historique: DroitAffiche[] };
  indivision: Indivision;
  restrictions: Array<{ id: string; type: string; description: string;
                        valide_du: string; valide_au: string | null;
                        beneficiaire_parcelle: string | null; emprise_partielle: boolean }>;
  titres: Array<{ id: string; identifiant: string; type_code: string; reference: string;
                  autorite_emettrice: string; date_emission: string; statut: string }>;
  litiges: Array<{ reference: string; objet: string; statut: string;
                   ouvert_le: string; clos_le: string | null }>;
  mutations: Array<{ reference: string; type: string; statut: string;
                     depose_le: string; applique_le: string | null }>;
  acces: { nominatif: boolean; sensibilite_accordee: number;
           restrictions_lisibles: boolean; titres_lisibles: boolean };
}

export interface TitulaireResume {
  id: string; denomination: string; type: string; identite_verifiee: boolean;
  nin_rattache: boolean; droits_actifs: number; proximite: string;
}

export interface FicheTitulaire {
  titulaire: Record<string, unknown> & { id: string; denomination: string; type: string };
  patrimoine: { actifs: BienDuTitulaire[]; historique: BienDuTitulaire[] };
  resume: string;
}

export interface BienDuTitulaire {
  parcelle: string; parcelle_statut: string; surface_m2: string; division: string | null;
  nature: string; quotite_num: number; quotite_den: number;
  valide_du: string; valide_au: string | null;
}

export interface DemandeRestriction {
  identifiantParcelle: string; type: string; description: string;
  beneficiaireParcelle?: string; motif: string;
}

/** Ce qu'une mutation FERA — la seule voie d'écriture des droits (§14). */
export interface EffetProjete {
  titulaires: Array<{ ref: string; type: string; denomination: string; referenceExterne?: string }>;
  droits: Array<{ titulaire: string; nature: string;
                  quotiteNum: number; quotiteDen: number; rang?: number }>;
  radier?: 'proprietes_actives' | string[];
}

/** L'effet d'une RECTIFICATION : un contour et sa qualité documentée — jamais
 *  des droits (effet-rectification.ts, D.1251). */
export interface EffetRectificationProjete {
  geometrie: unknown;
  origine: string;
  precisionM?: number;
  methode?: string;
  source?: string;
}

export interface TypesMutation {
  /**
   * ⚠️ `contrepartieAttendue` MANQUAIT ICI alors que le service l'envoie depuis
   * le §11. Le champ traversait le réseau et disparaissait au typage : l'écran
   * ne pouvait pas dire à l'agent qu'une vente exige un prix et une date d'acte,
   * là où une donation n'en exige pas. Un contrat d'écran en retard sur l'API
   * ne casse rien — il rend simplement une information invisible.
   */
  deposables: Array<{
    type: string;
    droitsAttendus: boolean;
    radiationAttendue: boolean;
    contrepartieAttendue: boolean;
    note: string;
  }>;
  nonConstruits: Array<{ type: string; raison: string }>;
}

export interface VariantesPlan {
  variantes: Array<{ cle: string; libelle: string; description: string }>;
  echelles: number[];
  formats: Array<{ cle: string; libelle: string; largeurMm: number; hauteurMm: number }>;
}

/**
 * Ce que le contrôle avant génération rend.
 *
 * ⚠️ TROIS VERDICTS, PAS DEUX — et l'écran doit les distinguer. `manquant`
 * dit « j'ai regardé, ce n'est pas là » ; `non_evaluable` dit « je n'ai pas pu
 * regarder ». Les afficher pareil rendrait une cécité indiscernable d'un
 * constat, et c'est précisément ce que le contrôle existe pour éviter.
 */
export interface ControlePlanUnitaire {
  cle: string;
  libelle: string;
  exigence: 'bloquant' | 'reserve' | 'informatif';
  verdict: 'satisfait' | 'manquant' | 'non_evaluable';
  detail: string;
  candidatsExamines: number;
}
export interface ControlesPlan {
  parcelle: string;
  etat: 'pret' | 'bloque';
  motifs: string[];
  reserves: string[];
  controles: ControlePlanUnitaire[];
  geometrie: { version: number; depuis: string | null; empreinte: string | null };
  couchesAbsentes: string[];
}

/**
 * §3 — ce que le plan de situation MONTRERA, dit avant de l'imprimer.
 *
 * ⚠️ `couches[].tronquee` EST LE CHAMP QUI COMPTE. Il dit que des objets
 * EXISTENT dans l'emprise et ne sont PAS dessinés. Sans lui, l'écran annoncerait
 * « 200 parcelles environnantes » sur une fenêtre qui en contient 885, et
 * personne n'aurait de raison d'en douter.
 */
export interface ControlesSituation {
  etat: 'pret' | 'bloque';
  motifs: string[];
  reserves: string[];
  controles: ControlePlanUnitaire[];
  cadres: Array<{
    cle: 'territoriale' | 'communale' | 'immediate';
    titre: string;
    echelle: number;
    parcelleHorsEchelle: boolean;
    couches: Array<{ cle: string; libelle: string; objets: number; source: string; tronquee: boolean }>;
  }>;
  couchesAbsentes: string[];
  referentiels: Array<{ couche: string; source: string; objets: number; dernierImport: string | null }>;
  rendudisponible: boolean;
}

/**
 * §11 — les plans de situation délivrés, avec l'emprise, les échelles, les
 * couches et la version des données de CHAQUE fenêtre. C'est ce qui permet de
 * prouver comment le document a été généré.
 */
export interface EditionsSituation {
  parcelle: string;
  editions: Array<{
    version: number;
    document: string;
    documentStatut: string;
    format: string;
    echelles: number[];
    cadres: unknown;
    referentiels: unknown;
    couchesAbsentes: string[];
    reserves: string[];
    versionGeometrie: number;
    empreinteGeometrie: string | null;
    empreinteComposition: string;
    empreinteSha256: string;
    tailleOctets: number;
    editeLe: string;
    traceInchange: boolean;
  }>;
}

/**
 * §5 — ce que le document d'arpentage établira, avant de l'éditer.
 *
 * ⚠️ `manques` EST LE CHAMP QUI COMPTE, et l'écran doit le montrer AVANT le
 * clic. Un agent qui découvre en ouvrant le PDF que l'auteur et la
 * justification sont absents a déjà promis une pièce à un usager.
 */
export interface EtatArpentage {
  parcelle: string;
  operation: 'division' | 'fusion' | 'rectification' | 'creation';
  operationLibelle: string;
  operationLe: string | null;
  avant: Array<{
    identifiant: string; version: number; surfaceM2: number;
    partAbsorbeePct: number | null; fermeeLe: string | null;
  }>;
  apres: Array<{ identifiant: string; version: number; surfaceM2: number }>;
  surfaceAvantM2: number;
  surfaceApresM2: number;
  ecartM2: number;
  mutation: string | null;
  affaire: string | null;
  auteur: string | null;
  justification: string | null;
  manques: string[];
  reserves: string[];
  rendudisponible: boolean;
}

export interface EditionsArpentage {
  parcelle: string;
  editions: Array<{
    version: number;
    operation: string;
    operationLibelle: string;
    document: string;
    documentStatut: string;
    etatAvant: unknown;
    etatApres: unknown;
    manques: string[];
    reserves: string[];
    cadre: unknown;
    format: string;
    empreinteComposition: string;
    empreinteSha256: string;
    tailleOctets: number;
    editeLe: string;
    /**
     * ⚠️ NE DIT PAS QUE LE DOCUMENT EST FAUX : dit que l'état d'arrivée a bougé
     * depuis, donc que ce papier décrit une étape qui n'est plus la dernière.
     */
    apresInchange: boolean | null;
  }>;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * LES BORNES DU LEVÉ
 * ═══════════════════════════════════════════════════════════════════════════ */

export interface BorneInscrite {
  numero: string;
  type: string;
  etat: string;
  cote: string | null;
  precisionM: number | null;
  observations: string | null;
  poseeLe: string | null;
}

/** Un point MESURÉ du levé — et la borne qu'il porte, ou rien. */
export interface PointDuLeve {
  indice: number;
  longitude: number;
  latitude: number;
  borne: BorneInscrite | null;
}

export interface BornesDuLeve {
  releve: string;
  statutReleve: string;
  parcelle: string | null;
  points: PointDuLeve[];
  inscrites: number;
  /**
   * ⚠️ LE VOCABULAIRE VIENT DU SERVICE, CODE **ET** LIBELLÉ. Le code est ce que
   * la route attend en retour, le libellé est ce qu'un agent lit. Quand l'écran
   * ne recevait que le code, il le rendait lisible lui-même — et c'est ainsi
   * que « non_materialisee » s'est imprimé sur le premier plan de bornage
   * réellement délivré du pays.
   */
  typesBorne: Array<{ code: string; libelle: string }>;
  etatsBorne: Array<{ code: string; libelle: string }>;
  inscriptible: boolean;
  /** ⚠️ « pas de point mesuré » et « brouillon » sont deux motifs différents. */
  motifNonInscriptible: string | null;
}

export interface DemandeInscriptionBornes {
  motif: string;
  bornes: Array<{
    indicePoint: number;
    numero: string;
    type: string;
    etat: string;
    cote?: string;
    precisionM?: number;
    observations?: string;
  }>;
}

export interface ResultatInscriptionBornes {
  releve: string;
  inscrites: number;
  bornes: Array<{ numero: string; indice: number; type: string; etat: string }>;
  /** ⚠️ Ce qui RESTE : un plan délivré sur un bornage partiel figure une limite incomplète. */
  pointsSansBorne: number;
  message: string;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * §6 — LE PROCÈS-VERBAL DE BORNAGE CONTRADICTOIRE
 *
 * ⚠️ CE QUE LE PV AJOUTE AU PLAN DE BORNAGE. Le plan imprime les avis
 * RECUEILLIS ; il ne peut pas montrer les voisins qui auraient DÛ être entendus
 * et ne l'ont pas été. C'est exactement là qu'une limite se déplace : on ne
 * convoque pas celui qui contesterait. Le PV porte les deux comptes face à
 * face — les contiguës que la GÉOMÉTRIE désigne, et les avis obtenus.
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * ⚠️ UN BOOLÉEN NE SUFFIT PAS, ET L'ÉCRAN NE DOIT PAS LE RÉDUIRE À UN. « Absent »
 * et « refus de signer » appellent une RECONVOCATION ; « opposition » ouvre une
 * instruction de LITIGE. Rendre « adjudication non acquise » sans dire laquelle
 * des deux envoie un dossier au mauvais guichet.
 */
export interface ObstaclePv {
  nature: 'contestation' | 'contradictoire_non_abouti' | 'parcelle_non_entendue' | 'identite_non_etablie';
  /** ⚠️ Jamais 0 : un obstacle sans cas ne s'inscrit pas. */
  compte: number;
  /** Ce qui a été VU, en clair — les côtés ou les identifiants concernés. */
  detail: string;
  /** Ce que cela appelle. C'est ce qui décide du guichet suivant. */
  suite: string;
}

export interface ContradictoirePv {
  etat: 'acquis' | 'non_acquis' | 'sans_objet';
  obstacles: ObstaclePv[];
  attendues: number;
  recueillis: number;
  accords: number;
  contestations: number;
  nonAboutis: number;
  /**
   * ⚠️ LA PHRASE QUI DIT LA POPULATION, PAS SEULEMENT LE VERDICT. « Aucun
   * obstacle » est trivialement vrai quand aucun avis n'a été recueilli.
   * L'écran l'affiche telle quelle — la résumer la viderait de ce qu'elle porte.
   */
  lecture: string;
}

export interface ComparantPv {
  cote: string;
  nom: string;
  source: 'registre' | 'declaration';
  verdict: string;
  /** ⚠️ VERBATIM, jamais reformulé par l'écran. */
  motif: string | null;
  parcelleVoisine: string | null;
  /**
   * ⚠️ UN AVIS NE FONDE UNE ADJUDICATION QUE S'IL VIENT DE QUELQU'UN
   * D'IDENTIFIÉ. Calculé par le service (`avisOpposable`), jamais ici.
   */
  opposable: boolean;
  pieceJointe: boolean;
}

export interface EtatPvBornage {
  releve: string;
  statutReleve: string;
  parcelle: string | null;
  premiereImmatriculation: boolean;
  contradictoire: ContradictoirePv;
  attendues: Array<{ parcelleId: string; identifiant: string; titulaires: number }>;
  comparants: ComparantPv[];
  bornes: Array<{
    numero: string; type: string; etat: string; cote: string | null;
    precisionM: number | null; observations: string | null; poseeLe: string | null;
  }>;
  /**
   * ⚠️ LES POINTS DU LEVÉ NE SONT PAS DES BORNES. `cad.borne` compte 0 ligne
   * pour tout le pays, tandis qu'un levé porte des points dans sa géométrie.
   * « 0 borne » sans cette distinction laisse croire que rien n'a été mesuré
   * sur le terrain. Deux états, deux suites : inscrire, ou y retourner.
   */
  pointsGeometrieLeve: number;
  manques: string[];
  reserves: string[];
  /**
   * ⚠️ UN PV EST TOUJOURS ÉDITABLE SAUF SUR UN BROUILLON, ET C'EST LE POINT.
   * Refuser d'éditer parce qu'un voisin s'oppose ne laisserait aucune trace
   * papier des contestations.
   */
  editable: boolean;
  motifNonEditable: string | null;
  rendudisponible: boolean;
}

export interface EditionsPvBornage {
  releve: string;
  editions: Array<{
    version: number;
    etatContradictoire: string;
    document: string;
    documentStatut: string;
    obstacles: ObstaclePv[];
    manques: string[];
    reserves: string[];
    format: string;
    empreinteComposition: string;
    empreinteSha256: string;
    tailleOctets: number;
    editeLe: string;
    /**
     * ⚠️ NE DIT PAS QUE LE PV EST FAUX : dit que les avis recueillis ont changé
     * depuis, donc que ce papier décrit un contradictoire qui n'est plus le
     * dernier. Sur cette pièce, c'est l'événement qu'une fraude produirait.
     */
    avisInchanges: boolean | null;
  }>;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * §7 — LA FICHE D'IDENTIFICATION DES PARTIES
 *
 * Elle confronte ce que la partie a DÉCLARÉ au guichet à ce que le REGISTRE
 * détient, et NOMME ce qui ne peut pas être confronté.
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * ⚠️ TROIS VERDICTS, JAMAIS DEUX. Avec une seule pièce d'identité déposée pour
 * cent titulaires, un binaire concordant/divergent rendrait « aucune
 * divergence » sur la totalité du pays : le vert obtenu faute d'avoir quoi
 * contredire.
 */
export type VerdictConfrontation = 'concordant' | 'divergent' | 'non_confrontable';

/**
 * ⚠️ `declare` N'EST PAS UNE NUANCE DE `etabli` : c'est l'écart entre « une
 * personne a dit s'appeler ainsi » et « une autorité l'a vérifié ». Les
 * confondre est précisément la fraude que le §7 vise.
 */
export type NiveauIdentification = 'etabli' | 'declare' | 'inconnu';

/** Comment la partie déclarée a été rattachée à un titulaire du registre. */
export type ModeRattachement = 'reference_externe' | 'nom' | 'aucun';

export interface ConfrontationFiche {
  element: string;
  declare: string;
  registre: string;
  verdict: VerdictConfrontation;
  /** Ce qui a été VU, jamais une conclusion : c'est ce qui rend le verdict rejouable. */
  pourquoi: string;
}

export interface PartieDeLaFiche {
  cle: string;
  role: string;
  libelle: string;
  nom: string;
  niveau: NiveauIdentification;
  /** ⚠️ `nom` ⇒ un nom, et rien d'autre, relie cette partie au registre. */
  rattachePar: ModeRattachement;
  titulaire: {
    id: string; denomination: string;
    identiteVerifiee: boolean; porteUnNin: boolean; pieces: number;
  } | null;
  confrontations: ConfrontationFiche[];
}

export interface EtatFicheParties {
  mutation: string;
  typeMutation: string;
  statutMutation: string;
  parcelle: string | null;
  affaire: string | null;
  /** Le dossier ne porte aucun formulaire exploitable : rien n'a pu être lu. */
  sansFormulaire: boolean;
  etat: {
    concordantes: number;
    divergentes: number;
    nonEvaluables: number;
    rapprochementsParLeNom: number;
    partiesEtablies: number;
    partiesDeclarees: number;
    partiesInconnues: number;
    /** ⚠️ La phrase qui dit la POPULATION, pas seulement le compte. */
    lecture: string;
  };
  parties: PartieDeLaFiche[];
  manques: string[];
  reserves: string[];
  /**
   * ⚠️ RENDU PAR LA MÊME FONCTION QUE LE REFUS DE LA ROUTE D'ÉDITION
   * (`blocageDeLaFiche`). L'écran ne recalcule pas la condition : deux
   * implémentations de la règle divergeraient au premier correctif, et la
   * divergence se verrait sous sa pire forme — un bouton qui promet une pièce
   * que le service refuse.
   */
  editable: boolean;
  motifNonEditable: string | null;
  rendudisponible: boolean;
}

/**
 * §10 — LE DOSSIER DE PIÈCES.
 *
 * ⚠️ TROIS ÉTATS, JAMAIS DEUX. « Absente » et « non prévue » ne se ressemblent
 * que pour qui ne les distingue pas : la première appelle un CLIC (le
 * générateur existe, personne n'a délivré), la seconde appelle un
 * DÉVELOPPEMENT (le registre ne sait pas encore produire cette pièce). Les
 * peindre pareil enverrait un agent chercher un bouton qui n'existe pas.
 */
export type EtatPiece = 'delivree' | 'absente' | 'non_prevue';

export interface PieceDuDossier {
  /** La section du `MASTER_PROMPT.md` qui commande cette pièce. */
  section: string;
  code: string;
  libelle: string;
  etat: EtatPiece;
  version: number | null;
  document: string | null;
  deliveeLe: string | null;
  /** Ce qui manque, en clair — jamais « absente » tout court. */
  pourquoi: string | null;
}

export interface DossierDePieces {
  parcelle: string | null;
  mutation: string | null;
  pieces: PieceDuDossier[];
  delivrees: number;
  absentes: number;
  nonPrevues: number;
  /** ⚠️ La phrase qui dit la POPULATION, pas seulement le compte. */
  lecture: string;
}

export interface EditionsFicheParties {
  mutation: string;
  editions: Array<{
    version: number;
    concordantes: number;
    divergentes: number;
    nonEvaluables: number;
    rapprochementsParLeNom: number;
    manques: string[];
    reserves: string[];
    format: string;
    empreinteComposition: string;
    empreinteSha256: string;
    tailleOctets: number;
    document: string;
    documentStatut: string;
    editeLe: string;
    /**
     * ⚠️ UNE BONNE NOUVELLE QUI PÉRIME LE PAPIER. Des pièces déposées DEPUIS
     * l'édition veulent dire que la fiche en circulation annonce encore « non
     * confrontable » là où le registre sait désormais quelque chose.
     */
    piecesDeposeesDepuis: number;
  }>;
}

/**
 * §11/§16 — les éditions archivées d'un plan.
 *
 * ⚠️ `traceInchange` NE DIT PAS QU'UN PLAN EST FAUX : il dit que la parcelle a
 * BOUGÉ depuis son édition. C'est l'événement même que le §11 demande de rendre
 * visible — sans lui, une liste de versions n'est qu'un historique de clics.
 */
export interface EditionPlan {
  variante: string;
  version: number;
  echelle: number;
  format: string;
  edite_le: string;
  version_geometrie: number;
  empreinte_geometrie: string;
  reserves: string[];
  document: string;
  document_statut: string;
  taille_octets: number;
  trace_inchange: boolean;
}
export interface EditionsPlan {
  parcelle: string;
  editions: EditionPlan[];
  lecture: string;
}

// ── §8 — le levé et son contrôle de qualité ─────────────────────────────────
export interface Releve {
  reference: string; statut: string; methode: string; precision_annoncee_m: number;
  depose_le?: string; decide_le?: string; parcelle?: string | null;
  surface_m2?: number; anomalies_ouvertes?: number;
}
export interface Controle {
  regle: string;
  /** ⚠️ TROIS états, jamais deux : « non évaluable » n'est pas « conforme ». */
  resultat: 'conforme' | 'anomalie' | 'non_evaluable';
  gravite: 1 | 2 | 3 | null;
  detail: Record<string, unknown>;
  /**
   * Combien d'objets la règle a réellement examinés. ⚠️ L'ÉCRAN DOIT LE
   * MONTRER : « 0 anomalie » ne veut rien dire sans « sur combien ». C'est la
   * différence entre un contrôle réussi et un contrôle qui n'a rien eu à
   * regarder — et c'est le motif le plus coûteux de ce dépôt.
   */
  candidatsExamines?: number;
  candidats_examines?: number;
  evalue_le?: string; traite_le?: string | null;
}
export interface Qualite {
  recevable: boolean; obstacles: string[]; candidatsExamines: number;
  conformes: number; anomalies: number; nonEvaluables: number; controles: Controle[];
}
export interface FicheReleve { releve: Record<string, any>; controles: Controle[] }
export interface DepotReleve {
  identifiantParcelle?: string; methode: string; precisionAnnonceeM: number;
  geometrie: unknown; surfaceDeclareeM2?: number; observations?: string;
}

export interface DepotLotLeves {
  corrections: Array<{ identifiantParcelle: string; geometrie: unknown }>;
  methode: string;
  precisionAnnonceeM: number;
  motif: string;
  toleranceM?: number;
}
export interface ResultatDepotLot {
  lot: string;
  membres: Array<{ releve: string; parcelle: string; recevable: boolean; obstacles: string[] }>;
  message: string;
}
export interface FicheLotLeves {
  lot: Record<string, unknown>;
  membres: Array<Record<string, unknown>>;
}
export interface ResultatDecisionLot {
  lot: string;
  statut: 'valide' | 'rejete';
  effets?: Array<{ releve: string; parcelle: string; surfaceM2: number }>;
  ecart_max_au_contact_m?: number | null;
  chevauchements?: number;
  message: string;
}
export interface ResultatDecision {
  releve: string; statut: string; message?: string;
  effet?: { identifiant: string; creee: boolean; surfaceM2: number; maillesAbsorbees: number; origineAvant: string | null };
  qualite?: Qualite;
}

// ── §15 — le registre documentaire ──────────────────────────────────────────
export interface DocumentLigne {
  identifiant: string; type_code: string; emetteur: string; date_document: string;
  version: number; statut: string; taille_octets: number; type_mime: string;
  sensibilite: number; extraction_statut: string; depose_le: string;
  empreinte_debut: string; rattachements: number;
}
/**
 * ⚠️ LE TÉMOIN DE COUVERTURE ACCOMPAGNE TOUTE RECHERCHE. « 0 résultat » sur un
 * fonds dont la moitié n'est pas indexable ne dit pas « la pièce n'existe
 * pas » : il dit « je n'ai pu regarder que la moitié ». L'écran doit montrer
 * les deux nombres, sinon l'agent conclut à une absence qu'on n'a pas prouvée.
 */
export interface CouvertureRecherche {
  total: number; cherchables: number; hors_portee_ocr: number; note: string;
}
export interface ListeDocuments {
  documents: DocumentLigne[]; tronque: boolean; limite: number;
  couvertureRecherche?: CouvertureRecherche;
}
export interface Rattachement {
  objet_type: string; objet_id: string; role: string; le: string; objet_libelle: string | null;
}
export interface JetonLigne {
  jeton_debut: string; emis_le: string; expire_le: string | null; revoque_le: string | null;
  consultations: number; derniere_consultation: string | null;
}
export interface FicheDocument {
  document: Record<string, any>; rattachements: Rattachement[]; jetons: JetonLigne[];
}
export interface DepotDocument {
  typeCode: string; emetteur: string; dateDocument: string; typeMime: string;
  contenuBase64: string; empreinteAnnoncee?: string; sensibilite?: number;
  rattachements: Array<{ objetType: string; objet: string; role?: string }>;
  remplaceIdentifiant?: string; motif: string;
}
export interface ResultatDepot {
  document: { identifiant: string; version: number };
  empreinteSha256: string; tailleOctets: number; deduplique: boolean; chiffre: boolean;
  indexation: { statut: string; motif: string };
  message: string;
}

// ── §37-§43 — les modes métier ──────────────────────────────────────────────
/**
 * ⚠️ CINQ ÉTATS, ET « ZÉRO » N'EN EST PAS UN. `rien_en_attente` et
 * `sans_matiere` valent tous deux 0 et ne disent pas la même chose : le premier
 * est une bonne nouvelle sur un registre qui vit, le second dit seulement que
 * personne n'a rien déposé. L'écran DOIT les rendre différemment — les
 * confondre, c'est afficher du vert obtenu faute d'avoir trouvé quoi contredire.
 */
export type EtatTuile = 'a_traiter' | 'rien_en_attente' | 'sans_matiere' | 'masque' | 'non_implemente';
/** §adjudication contradictoire — l'avis des voisins sur un contour proposé. */
export type VerdictRiverain = 'concordant' | 'opposition' | 'absent' | 'refus_de_signer';

export interface Riverain {
  id: string; cote: string; verdict: VerdictRiverain; motif: string | null;
  recueilli_le: string;
  parcelle_voisine_id: string | null; parcelle_voisine: string | null;
  titulaire_id: string; denomination: string; titulaire_type: string;
  identite_verifiee: boolean;
  /** Décodés du NIN lui-même : le numéro guinéen les encode. */
  nin_date_naissance: string | null; nin_code_lieu: string | null;
  porte_un_nin: boolean;
  document_id: string | null;
}

export interface Adjudication {
  releve: string;
  premiere_immatriculation: boolean;
  /** Calculé par la GÉOMÉTRIE, jamais déclaré. Sans lui, « tous ont signé » est vide. */
  attendus: number;
  recueillis: number;
  riverains: Riverain[];
  parcelles_non_entendues: Array<{ parcelle_id: string; identifiant: string; titulaires: string }>;
  bloquants: Riverain[];
  sans_identite: Riverain[];
  adjudication_acquise: boolean;
  lecture: string;
}

export interface RecueilRiverain {
  titulaireId: string; parcelleVoisineId?: string; cote: string;
  verdict: VerdictRiverain; motif?: string; documentId?: string;
}

export type PorteeCompteur = 'nationale' | 'divisions' | 'non_scopable';

export interface Tuile {
  compteur: string; libelle: string; aide: string; rubrique: string | null;
  ressource: string | null; operation: string | null;
  etat: EtatTuile; valeur: number | null; population: number | null;
  portee: PorteeCompteur | null; motif?: string;
}
export interface ModeMetier {
  code: string; libelle: string; section: string; resume: string;
  rubriqueAtterrissage: string;
  rubriques: string[]; compteurs: string[];
  /** Rubriques déclarées en base que cette interface ne sait pas dessiner. */
  rubriquesInconnues: string[];
  parDefaut: boolean; compteursLisibles: number; accessible: boolean;
}
export interface CatalogueModes {
  /** ⚠️ MES modes, pas le catalogue. Le service ne rend plus les sept. */
  modes: ModeMetier[];
  modeParDefaut: string | null;
  /** Aucun rôle de cet agent n'atterrit sur un mode — voulu pour citoyen/titulaire. */
  sansMode: boolean;
  roles: string[];
  /**
   * D'où vient mon métier : de mon AFFECTATION (le cas normal), ou à défaut de
   * mes rôles quand aucun service ne m'est rattaché.
   */
  origineDuMode: 'service' | 'role' | null;
  /**
   * ⚠️ Le rôle revendique un métier, l'affectation en tient un autre. Rendu,
   * jamais tu : sans cette phrase, l'agent croit que son écran se trompe.
   */
  discordance: string | null;
}
export interface PlanTravail {
  mode: { code: string; libelle: string; section: string; resume: string; rubriqueAtterrissage: string };
  tuiles: Tuile[];
  portee: { type: 'nationale' | 'divisions'; divisions: number };
  mesure_le: string;
}


// ═══════════════════════════════════════════════════════════════════════════
// §10-§13 — LES TYPES DU MARCHÉ, DES COPROPRIÉTÉS ET DE L'ASSIETTE
// ═══════════════════════════════════════════════════════════════════════════

export type { Nature, Valeur } from '../../src/partage/nature-valeur.js';
import type { Valeur } from '../../src/partage/nature-valeur.js';

export interface TransactionAffichable {
  id: string; parcelle: string; nature: string;
  montant: number | null; devise: string | null;
  dateTransaction: string | null; dateEstimee: boolean;
  surfaceReferenceM2: number | null; prix_m2?: number | null;
  statut?: string; parcelleCourante?: boolean; mutation?: string | null;
}

export interface StatistiquesPrix {
  effectif_total: number; effectif_retenu: number;
  ecartees: { nature_non_marchande: number; sans_prix: number; sans_surface: number };
  devise: string | null;
  prix_median: Valeur; prix_m2_median: Valeur;
  prix_m2_q1: Valeur; prix_m2_q3: Valeur;
  prix_m2_min: Valeur; prix_m2_max: Valeur;
  seuils: { mediane: number; quartiles: number };
}

export interface ReponseStatistiques { filtres: Record<string, unknown>; statistiques: StatistiquesPrix }
export interface ReponseTransactions {
  filtres: Record<string, unknown>; effectif: number; tronquee: boolean;
  transactions: TransactionAffichable[];
}
export interface PointTendance {
  periode: string; effectif: number; prix_m2_median: Valeur; variation_pct: Valeur;
}
export interface Tendance {
  granularite: string; points: PointTendance[];
  projection_periode_suivante: Valeur; sans_date: number; date_estimee: number;
}
export interface ReponseTendances { tendance: Tendance }
export interface Anomalie {
  transaction: TransactionAffichable; prixM2: number; score: number;
  ecart_a_la_mediane_pct: number; sens: 'sous_evaluee' | 'sur_evaluee';
}
export interface ReponseAnomalies {
  /** ⚠️ `null` = on ne peut pas conclure. JAMAIS à confondre avec `[]`. */
  anomalies: Anomalie[] | null;
  diagnostic: Valeur; mediane_m2: Valeur; seuil_score: number; lecture: string;
}
export interface Comparable {
  transaction: TransactionAffichable; prixM2: number; proximite: number;
  criteres: { meme_division: boolean; ecart_surface_pct: number | null; anciennete_jours: number | null };
}
export interface ReponseComparables {
  cible: { parcelle: string | null; surface_m2: number | null; division_id: string | null };
  univers: number; comparables: Comparable[]; valeur_estimee: Valeur; criteres_appliques: string[];
}
export interface ReponseSecteur {
  secteur: { id: string | null; nom: string; code: string | null };
  statistiques: StatistiquesPrix;
  tendance: Tendance;
  anomalies: Partial<ReponseAnomalies> & { champs_masques?: string[] };
}
export interface ChaineParcelle {
  parcelle: { identifiant: string; surface_m2: number | null; statut: string; division_id: string | null };
  historique?: TransactionAffichable[];
  champs_masques: string[];
  nombre_transactions: number;
  statistiques: StatistiquesPrix;
  chaine: {
    annonces: Array<{ bien_id: string; statut: string; verifie_le: string | null }>;
    batiments: Array<{ id: string; identifiant: string; usage: string | null; niveaux: number | null }>;
    documents: Array<{ id: string; identifiant: string; type_code: string; depose_le: string }>;
    objets_fiscaux: Array<{ fiscal_property_id: string; statut: string; ecart_surface_pct: number | null }>;
    coproprietes: Array<{ identifiant: string; denomination: string; statut: string; lots: number }>;
  };
  signaux: { annonces_incoherentes: number; transactions_sans_prix: number; sans_objet_fiscal: boolean };
}

// ── §12 ─────────────────────────────────────────────────────────────────────
export interface EtatStructure { complete: boolean | null; lecture: string }
export interface CoproprieteResume {
  identifiant: string; denomination: string; adresse: string | null; parcelle: string;
  statut: string; lots_deposes: number; lots_declares: number | null; structure: EtatStructure;
}
export interface ListeCoproprietes {
  criteres: Record<string, unknown>; effectif: number; tronquee: boolean;
  coproprietes: CoproprieteResume[];
}
export interface FicheCopropriete {
  copropriete: {
    identifiant: string; denomination: string; adresse: string | null; regime: string | null;
    date_reglement: string | null; statut: string; parcelle: string;
    lots_declares: number | null; lots_deposes: number;
  };
  structure: EtatStructure;
  lots?: Array<{ numero: string; type: string; etage: number | null; surface_m2: number | null;
                 tantieme: string; batiment_id: string | null; bien_id?: string | null }>;
  parties_communes?: Array<{ designation: string; categorie: string; surface_m2: number | null }>;
  batiments?: Array<{ id: string; identifiant: string; usage: string | null; niveaux: number | null }>;
  tantiemes?: { num: number; den: number; complet: boolean; excedentaire: boolean;
                reste: { num: number; den: number } | null; parts: number; lecture: string };
  documents: Array<{ id: string; identifiant: string; type_code: string; depose_le: string }>;
  champs_masques: string[];
}

// ── §13 ─────────────────────────────────────────────────────────────────────
export interface GrapheFiscal {
  parcelle: { identifiant: string; surface_mesuree_m2: number | null; statut: string; division_id: string | null };
  rattachement_fiscal: { etat: 'aucun_lien' | 'lie'; lecture?: string; nombre?: number };
  objets_fiscaux: Array<{
    fiscal_property_id: string; juridiction: string | null; statut: string; lecture: string;
    ecart_surface_pct: number | null; surface_constatee_m2: number | null;
    surface_declaree_m2?: number | null; verifie_le: string | null;
  }>;
  graphe: {
    batiments: Array<{ id: string; identifiant: string; usage: string | null; niveaux: number | null }>;
    biens_immobiliers: Array<{ bien_id: string; statut: string }>;
    coproprietes: Array<{ identifiant: string; denomination: string; statut: string; lots: number }>;
    titulaires?: Array<{ denomination: string; type: string; nature: string;
                         quotite_num: number; quotite_den: number; taxpayer_id: string | null }>;
  };
  non_detenu_par_le_cadastre: { champs: string[]; lecture: string };
  champs_masques: string[];
}
export interface SyntheseFiscale {
  parcelles_actives: number; parcelles_liees: number;
  couverture: { taux_pct: number | null; lecture: string };
  liens_incoherents: number; liens_non_verifiables: number; ecarts_superieurs_10pct: number;
  lecture_non_verifiables: string;
  service_fiscal: { joignable: boolean; motif: string | null; lecture: string };
}
export interface ResolutionFiscale {
  trouve: boolean; voie: 'lien_etabli' | 'reference_declaree';
  fiabilite?: 'verifiee' | 'declarative'; avertissement?: string; lecture?: string;
  parcelle?: string; statut_parcelle?: string; statut_lien?: string;
  carte?: { longitude: number | null; latitude: number | null; ouvrir: string };
}

// ── Trois routes d'écriture qui n'avaient pas d'écran ───────────────────────

/** Corps de POST /parcelles/:identifiant/activer. */
export interface DemandeActivation { motif: string; precisionM?: number }
/**
 * ⚠️ `mailles_absorbees` peut valoir 0 — ce n'est pas un échec, c'est une
 * parcelle qui ne recouvrait aucune maille présomptive. `absorption` n'existe
 * QUE si des mailles ont cédé (voir src/modules/parcelles.ts:260).
 */
export interface ResultatActivation {
  parcelle: string; statut: string; surface_m2: number | null; precision_m: number | null;
  mailles_absorbees: number; absorption?: string;
  /** Présent seulement si la parcelle était déjà active : rien n'a été écrit. */
  message?: string;
}

/** Corps de POST /territoire/parcelles/:identifiant/revendication. */
export interface DemandeRevendication {
  titulaireId?: string;
  qualite: 'occupant' | 'heritier' | 'acquereur' | 'coutumier' | 'locataire' | 'autre';
  pieces?: string[];
  motif: string;
}
/**
 * ⚠️ Une revendication n'est jamais un droit — le service le répète dans
 * `avertissement`, l'écran ne le recompose pas.
 */
export interface ResultatRevendication {
  revendication: string; parcelle: string; qualite: string;
  revendications_sur_cette_parcelle: number;
  /** Présent seulement si plusieurs revendications concurrentes existent déjà. */
  alerte?: string;
  avertissement: string;
}

/** Corps de POST /graphe/fiscal. */
export interface DemandeRattachementFiscal {
  identifiantParcelle: string; fiscalPropertyId: string; juridiction?: string;
}
/** Un contrôle du moteur de rapprochement (src/domaine/rapprochement.ts) —
 *  forme distincte du `Controle` du §8 : `conforme` est un booléen ou `null`,
 *  jamais un `resultat` textuel. */
export interface ControleRapprochement {
  regle: string; conforme: boolean | null; motifNonEvaluable?: string;
  observe?: string | number | null; attendu?: string | number | null; ecart?: number;
}
export interface ResultatRapprochementFiscal {
  parcelle: string;
  /** Quatre états, jamais deux : `non_verifiable` est la réponse ATTENDUE
   *  tant que le service Mowibo Fiscal n'écoute pas (voir fiscal.ts). */
  verdict: 'verifie' | 'incoherent' | 'non_verifiable' | 'declare';
  controles: ControleRapprochement[];
  controlesExecutes: number; controlesEnAnomalie: number;
  resume: string; ecartSurfacePct: number | null;
}

// ── Le dossier d'immatriculation (§31, §34) ─────────────────────────────────

export interface ActionDossier {
  code: 'publicite' | 'opposition' | 'immatriculer' | 'regulariser_titre'
      | 'certificat' | 'certificat_acte';
  libelle: string;
  ouverte: boolean;
  /** Pourquoi c'est fermé — permission OU règle métier. Jamais vide si fermé. */
  motif?: string;
}

export interface Opposition {
  reference: string; statut: string; decision: string | null;
  opposant_nom: string; objet: string;
  ouvert_le: string; clos_le: string | null; motif_clos: string | null;
  bloquante: boolean;
  mutation?: string; parcelle?: string;
}

export interface Dossier {
  mutation: {
    id: string; reference: string; type: string; statut: string;
    parcelle: string | null; depose_le: string | null;
    /** ⚠️ « validée » ne dit PAS que l'acte a écrit au registre ; ceci le dit. */
    applique_le: string | null;
  };
  approbations: Array<{
    decision: string; autorite_type: string | null; autorite_nom: string | null;
    decide_le: string; commentaire: string | null;
  }>;
  approbationsDistinctes: number;
  avisManquants: string[];
  publicite: { support: string; ouverte_le: string; delai_jours: number; echeance: string; echue: boolean } | null;
  oppositions: Opposition[];
  titre: { identifiant: string; date_emission: string; statut: string } | null;
  blocages: string[];
  /**
   * ⚠️ CE QUE L'APPELANT NE VOIT PAS EST NOMMÉ. Une liste vide parce qu'on n'a
   * pas le droit de la lire se lirait « il n'y a rien » — donc « la voie est
   * libre ». Ici l'écran l'affiche.
   */
  blocs_masques: string[];
  actions: ActionDossier[];
}

export interface ResultatCertificat {
  titre: string; document: string; jeton: string;
  empreinte: string; empreinteDonnees: string; octets: number;
  format: string; urlVerification: string; urlTelechargement: string;
  message: string;
}

export interface ResultatCertificatActe {
  acte: string; type: string; autorite: string;
  document: string; jeton: string; octets: number;
  empreinte: string; empreinteDonnees: string;
  format: string; urlVerification: string; urlTelechargement: string;
  message: string;
}

// ── Le dossier (§31) ────────────────────────────────────────────────────────

export interface DossierLigne {
  reference: string; objet: string; etape_courante: string;
  ouvert_le: string; clos_le: string | null;
  parcelle: string | null; requerant: string; actes: number;
  depuis?: unknown;
}

export interface EtapeDossier {
  etape: string; survenu_le: string; acteur: string | null;
  objet_type: string | null; objet_id: string | null;
  commentaire: string | null; detail: Record<string, unknown>;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LA FICHE D'UNE AFFAIRE — DEUX FORMES, ET C'EST UN TYPE UNION.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️⚠️ UN SEUL TYPE POUR DEUX RÉPONSES A FAIT UNE PAGE BLANCHE. `GET
 * /affaires/:reference` rend la fiche complète, OU — quand l'affaire ne relève
 * pas de la commune de l'agent — une vue « existence » sans requérant, sans
 * parcelle et sans objet. Ce type ne décrivait que la première : `requerant`
 * y était NON optionnel, `tsc` était donc vert, et l'écran lisait
 * `f.requerant.denomination` sur `undefined`. React démontait tout l'arbre :
 * l'agent voyait une page BLANCHE là où le service disait correctement
 * « vous en voyez l'existence, sans aucune donnée nominative ».
 *
 * ⚠️ L'UNION DISCRIMINÉE N'EST PAS UNE ÉLÉGANCE : c'est ce qui fait ÉCHOUER LE
 * TYPAGE tant que l'écran ne traite pas les deux cas. Un champ rendu optionnel
 * aurait laissé passer le même oubli avec un `?.`.
 */
export interface FicheAffaireExistence {
  visibilite: 'existence';
  affaire: {
    reference: string; etape: string;
    ouvert_le: string; clos_le: string | null; vivant: boolean;
  };
  /** La commune COMPÉTENTE — celle du fonds, pas celle de l'agent. */
  commune: string | null;
  /** Pourquoi on ne montre pas : une règle, pas une panne. */
  motif: string;
  blocs_masques: string[];
  impression: { possible: boolean; motif: string };
}

export interface FicheDossier {
  visibilite: 'complet';
  affaire: {
    reference: string; objet: string; etape: string;
    ouvert_le: string; ouvert_par: string | null;
    clos_le: string | null; motif_clos: string | null; vivant: boolean;
  };
  requerant: { denomination: string; type: string; identifie: boolean; mowibo_id_sujet?: string | null };
  parcelle: { identifiant: string; surface_m2: number } | null;
  fil: EtapeDossier[];
  actes: Array<{ reference: string; type: string; type_libelle: string; statut: string;
                 depose_le: string; approbations: number }>;
  leves: Array<{ reference: string; statut: string; methode: string;
                 precision_annoncee_m: number; depose_le: string }>;
  acteurs: Array<{ role_tenu: string; depuis_le: string; compte: string; organisation: string | null }>;
  documents: Array<{ identifiant: string; type_code: string; type_mime: string;
                     statut: string; date_document: string }>;
  titre: { identifiant: string; date_emission: string; statut: string } | null;
  /** La demande qui a ouvert le dossier. `null` pour une ouverture directe au
   *  guichet — c'est une information, pas une lacune. */
  demande: { reference: string; type_code: string; libelle: string; etat: string;
             service_courant: string | null; circuit: string[] } | null;
  recepisse: {
    document: string; envoi_reference: string | null; envoi_etat: string | null;
    echec_motif: string | null; edite_le: string; expedie_le: string | null;
  } | null;
  /** Ce que l'habilitation ne couvre pas — NOMMÉ, jamais omis en silence. */
  blocs_masques: string[];
}

/** Ce que rend la route : l'une OU l'autre forme. */
export type FicheAffaire = FicheDossier | FicheAffaireExistence;

export interface ResultatOuverture {
  affaire: string; requerant: string; parcelle: string | null; etape: string;
  recepisse: { du: boolean; expediable: boolean; motif: string | null };
  message: string;
}

export interface ResultatRecepisse {
  affaire: string; document: string; rejeu?: boolean;
  format?: string; octets?: number; empreinte?: string;
  urlVerification?: string; urlTelechargement: string;
  expedition?: { etat: string; reference: string | null; motif: string | null };
  envoi_etat?: string | null; echec_motif?: string | null;
  message: string;
}

export interface ResultatInscription {
  titulaire: { id: string; denomination: string; type: string };
  identifie: boolean;
  identiteVerifiee: boolean;
  joignableParCourrier: boolean;
  /** Personnes portant déjà ce nom — la base ne peut pas les départager sans NIN. */
  homonymes: Array<{ id: string; denomination: string }>;
  message: string;
}

// ── Le guichet (§31) ────────────────────────────────────────────────────────

export interface TypeDemande {
  code: string; libelle: string; objet: string;
  circuit: string[]; canaux: string[];
  delai_jours: number | null; produit: string | null;
  /** Mon guichet peut-il déposer ceci — et sinon, POURQUOI. */
  deposable: boolean; motifFerme: string | null;
  formulaireDisponible: boolean;
}
export interface CatalogueDemandes {
  monService: string | null; monOrganisation: string | null; types: TypeDemande[];
}

export interface LigneFile {
  reference: string; type_code: string; libelle: string; etat: string;
  depose_le: string; position_circuit: number; circuit: string[];
  delai_jours: number | null; affaire: string; requerant: string;
  parcelle: string | null; jours_depuis_depot: number; delai_depasse: boolean;
}
export interface FileDemandes {
  monService: string | null; monOrganisation?: string;
  demandes: LigneFile[]; total: number; enRetard?: number; motif?: string;
}

export interface FicheDemande {
  demande: {
    reference: string; type: string; libelle: string; objet: string;
    etat: string; canal: string; depose_le: string;
    close_le: string | null; motif_clos: string | null;
    produit: string | null; delaiJours: number | null;
  };
  affaire: { reference: string; etape: string };
  requerant: string;
  parcelle: string | null;
  formulaire: Record<string, unknown>;
  circuit: { services: string[]; position: number; courant: string | null; suivant: string | null };
  parcours: Array<{ de_service: string | null; vers_service: string | null;
                    motif: string | null; survenu_le: string;
                    acteur: string | null; organisation: string | null }>;
  /** Le SERVICE dit si la demande est chez l'appelant — l'écran ne le déduit pas. */
  chezMoi: boolean; motifSiPasChezMoi: string | null;
}

export interface ResultatDepotDemande {
  demande: string; affaire: string; type: string; libelle: string;
  requerant: string; parcelle: string | null;
  transmiseA: string; circuit: string[];
  delaiJours: number | null; produit: string | null; message: string;
}

export interface FluxMetier {
  monService: string | null;
  /** ⚠️ Sert à distinguer « rien n'attend » de « rien n'est jamais passé ». */
  population: {
    demandes_total: number; demandes_vivantes: number;
    dossiers_total: number; dossiers_vivants: number; titres_delivres: number;
  };
  parcours: Array<{ etape: string; libelle: string; qui: string; quoi: string;
                    affaires: number; plusAncienJours: number | null }>;
  circuits: Array<{
    code: string; libelle: string; delaiJours: number | null;
    produit: string | null; canaux: string[];
    postes: Array<{ rang: number; service: string; enAttente: number;
                    plusAncienJours: number | null; enRetard: number; chezMoi: boolean }>;
  }>;
  bloques: Array<{ reference: string; type: string; statut: string;
                   avis_attendu: string; donneurs_possibles: number;
                   inatteignable: boolean; remede: string | null }>;
}


/* ═══════════════════════════════════════════════════════════════════════════
   §19 — ANOMALIES DE COHÉRENCE ET CHRONOLOGIE JURIDIQUE

   ⚠️ CES TYPES DÉCRIVENT CE QUE LE SERVICE REND, ils ne le décident pas. Les
   phrases (`lecture`, `explication`, `impact.lecture`, `prochaine_action`)
   viennent du SERVICE et sont affichées TELLES QUELLES : un écran qui
   recomposerait « bloqué » ou « sans objet » à partir des champs bruts
   divergerait de la garde au premier correctif appliqué d'un seul côté.
   ═══════════════════════════════════════════════════════════════════════════ */

export type CodeDecisionAnomalie = 'corrigee' | 'faux_positif' | 'acceptee_avec_reserve';

export interface FicheRegleAnomalie {
  code: string; libelle: string; pourquoi: string; risque: string;
  service: string; action: string; limiteConnue?: string;
}

export interface AnomalieFile {
  id: string; regle: string; gravite: number | null; bloquante: boolean;
  detail: Record<string, unknown> | null;
  /** Le canari : combien d'objets la règle a réellement examinés. */
  candidats_examines: number;
  evalue_le: string; age_jours: number;
  objet: { type: string; parcelle: string | null; parcelle_statut: string | null;
           parcelle_origine: string | null; releve: string | null; division: string | null;
           non_situable?: string; echafaudage?: string };
  explication: FicheRegleAnomalie | null;
  inexpliquee?: string;
  impact: { affaire: string | null; affaire_etape: string | null;
            mutations_en_cours: number; mutations_bloquees: string | null; lecture: string };
  echeance: { le: string; jours_restants: number; fondement: string } | null;
  echeance_absente?: string;
  traitement?: { le: string; par: string; decision: string | null;
                 decision_libelle: string | null; motif: string | null;
                 par_le_service?: boolean; anterieur?: string };
}

export interface SyntheseAnomalies {
  ouvertes: number; bloquantes: number; traitees: number; population: number;
  /** Closes par la DÉCISION d'un agent · par le SERVICE (contrôle plus récent) · avant la 076. */
  decidees: number; remplacees: number; closes_sans_decision: number;
  conformes_aveugles: number; non_evaluables: number; hors_portee_mesurable: number;
  par_regle: Array<{ regle: string; gravite: number | null; ouvertes: number;
                     bloquante: boolean; fiche: FicheRegleAnomalie | null; inexpliquee?: string }>;
  regles_inexpliquees?: string[];
  portee: { type: 'nationale' | 'divisions'; divisions?: number };
  avertissement_portee?: string;
  lecture: string;
}

export interface ResultatTraitement {
  traite: boolean; decision: string; decision_libelle: string; sens: string;
  regle: string; parcelle: string | null;
  reste_ouvertes: number; reste_bloquantes: number; message: string;
  suite_attendue?: string;
}

/** Les SIX états d'une phase. Aucun ne se replie sur un autre. */
export type EtatPhase = 'faite' | 'en_cours' | 'bloquee' | 'a_faire' | 'sans_objet' | 'non_observable';

export interface PhaseProcedure {
  code: string; libelle: string; etat: EtatPhase; lecture: string;
  le: string | null; responsable: string | null;
  pieces: Array<{ id: string; nom: string; role: string | null; le: string }>;
  decisions: Array<{ quoi: string; par: string | null; le: string; commentaire?: string | null }>;
  anomalies: Array<{ id: string; regle: string; libelle: string; gravite: number | null; bloquante: boolean }>;
  echeance: { le: string; jours_restants: number; fondement: string } | null;
  prochaine_action: string | null;
  couverture?: { regles_voyantes: number; regles_aveugles: string[] };
}

export interface ProcedureParcelle {
  reference: string; origine: 'dossier' | 'acte_hors_affaire';
  type: string | null; libelle: string; statut: string;
  ouverte_le: string | null; close_le: string | null;
  demande: { reference: string; type: string; libelle: string; etat: string;
             service_courant: string | null; position_circuit: number; circuit: string[];
             depose_le: string; canal: string | null; delai_jours: number | null } | null;
  affaire: { reference: string; etape_courante: string; ouvert_le: string; clos_le: string | null } | null;
  acte: { reference: string; type: string; statut: string;
          depose_le: string | null; applique_le: string | null } | null;
  fil: Array<{ etape: string; survenu_le: string; commentaire: string | null; acteur: string | null }>;
  phases: PhaseProcedure[];
  blocages: string[];
  prochaine_action: string | null;
  hors_dossier?: string;
}

export interface TitulaireActuel {
  denomination: string; type: string; nature: string; quotite: string;
  rang: number | null; depuis: string; titre: string | null;
  inscrit_par: string | null; sans_titre?: string;
}

export interface EtatJuridique {
  titulaires_actuels: TitulaireActuel[] | { masque: true; nombre: number; motif: string };
  droits_radies: Array<{ denomination: string; nature: string; du: string; au: string;
                         radie_par: string | null; radiation_sans_acte?: string }>
                 | { masque: true; nombre: number; motif: string };
  titres: Array<{ identifiant: string; reference: string; type: string;
                  autorite: string; date_emission: string; statut: string }>;
  restrictions: Array<{ type: string; description: string }>;
  litiges: Array<{ reference: string; objet: string; statut: string; ouvert_le: string }>;
  anomalies: { ouvertes: number; bloquantes: number;
               liste: Array<{ id: string; regle: string; libelle: string;
                              gravite: number | null; bloquante: boolean }> };
  lecture: string;
}

export interface ChronologieParcelle {
  parcelle: { identifiant: string; statut: string; surface_m2: string;
              division: string | null; valide_du: string };
  etat_juridique: EtatJuridique;
  procedures: ProcedureParcelle[];
  lecture?: string;
  chronologie: Array<{ horodatage: string; action: string; objet_type: string;
                       motif: string | null; acteur: string | null }>;
}


/* ═══════════════════════════════════════════════════════════════════════════
 * LOT B — L'ACTE COMMUNAL
 * ═══════════════════════════════════════════════════════════════════════════ */

export interface ReglagesAttestation {
  roles: string[];
  origines: string[];
  piecesIdentite: string[];
  autorite: { type_organisation: string; libelle: string; fondement: string } | null;
  avertissement: string;
}

export interface AttestationResume {
  reference: string; statut: string; commune: string;
  region: string; prefecture: string; sous_prefecture: string;
  acquereur: string; parcelle: string | null;
  superficie_m2: number | null; cree_le: string; delivree_le: string | null;
  opposition_recue: boolean; parties: number; manques: number;
}

export interface ListeAttestations {
  attestations: AttestationResume[]; total: number; rendus: number; message?: string;
}

export interface PartieAttestation {
  id: string; role: string; agit_pour: string | null;
  signe_le: string | null; signe_lieu: string | null;
  signe_lat: number | null; signe_lon: number | null;
  piece_identite_type: string | null; piece_identite_numero: string | null;
  a_signature: boolean; a_empreinte: boolean; a_photo: boolean;
  titulaire_id: string; denomination: string; titulaire_type: string;
  identite_verifiee: boolean; mowibo_mid: string | null;
  /** ⚠️ JAMAIS LE NUMÉRO — l'empreinte, les quatre derniers, la source, l'état. */
  nin_rattache: boolean; nin_quatre_derniers: string | null;
  nin_source: string | null; nin_verifie_le: string | null;
}

export interface FicheAttestation {
  attestation: {
    reference: string; statut: string; commune: string;
    region: string; prefecture: string; sousPrefecture: string;
    district: string | null; secteur: string | null;
    parcelle: string | null; contour: unknown; contourSurfaceM2: number | null;
    ancrage: { lon: number; lat: number };
    lotNumero: string | null; longueurM: number | null; largeurM: number | null;
    superficieM2: number | null;
    acquereur: { id: string; denomination: string; type: string };
    riverains: { nord: string | null; sud: string | null; est: string | null; ouest: string | null };
    origine: { propriete: string | null; precision: string | null; detenuDepuis: string | null;
               anterieure: { reference: string; statut: string } | null };
    transaction: { aTitreOnereux: boolean | null; prixDeclare: number | null;
                   devise: string | null; modePaiement: string | null; preuvePaiement: boolean };
    declarations: { sansLitige: boolean | null; sansHypotheque: boolean | null;
                    sansVenteAnterieure: boolean | null; chargesEtServitudes: string | null };
    conjoint: { titulaireId: string | null; le: string | null; situationMatrimoniale: string | null };
    registreCommunal: { numero: string | null; page: string | null };
    affichage: { du: string | null; au: string | null; oppositionRecue: boolean };
    pieces: { croquisTerrain: string | null; photoTerrain: string | null;
              photoCedant: string | null; photoAcquereur: string | null };
    affaire: string | null; documentId: string | null;
    creeLe: string; delivreeLe: string | null;
    annuleeLe: string | null; motifAnnulation: string | null;
  };
  parties: PartieAttestation[];
  /** ⚠️ CALCULÉ PAR LA BASE, jamais par l'écran (`fon.attestation_manques`). */
  manques: string[];
  delivrable: boolean;
  inscriptionPlanFoncier: { inscrite_le: string; numero_ordre: string | null;
                            collectivite: string; radiee_le: string | null } | null;
}

export interface ReponseAttestation {
  attestation: string; statut?: string; manques?: string[]; message?: string;
  [k: string]: unknown;
}

export interface ReponseDelivrance extends ReponseAttestation {
  document: string; jeton: string; empreinte: string; empreinteDonnees: string;
  urlVerification: string; urlTelechargement: string;
  inscritAuPlanFoncier: boolean;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * LOT C — LE LIVRE FONCIER
 * ═══════════════════════════════════════════════════════════════════════════ */

export interface TrouDuLivre<T> { nombre: number; lignes: T[]; lecture: string }

export interface TableauLivre {
  etat: {
    feuilles_ouvertes: number; feuilles_closes: number;
    mentions: number; mentions_vivantes: number;
    depots: number; depots_en_instance: number; copies: number; titres_valides: number;
  };
  autorite: { type_organisation: string; libelle: string; fondement: string } | null;
  volumeCourant: string;
  trous: {
    titresSansFeuille: TrouDuLivre<{ numero_titre: string; acte_origine: string;
      date_emission: string; autorite_emettrice: string; parcelle: string | null; droits_actifs: number }>;
    droitsHorsLivre: TrouDuLivre<{ numero_titre: string; parcelle: string; nature: string;
      quotite_num: number; quotite_den: number; titulaire: string; mutation: string | null }>;
    depotsEnInstance: TrouDuLivre<{ volume: string; numero: number; nature_acte: string;
      objet_type: string; suite: string; depose_le: string; numero_titre: string | null }>;
  };
}

export interface ReglagesLivre {
  autorite: { objet: string; type_organisation: string; libelle: string; fondement: string } | null;
  volumeCourant: string;
  naturesMention: string[];
  categoriesPiece: string[];
  peut: { ouvrirFeuille: boolean; clore: boolean; rectifier: boolean;
          delivrerCopie: boolean; deposer: boolean };
}

export interface FeuilleResume {
  numero_titre: string; volume: string | null; folio: string | null;
  statut: string; ouverte_le: string; close_le: string | null;
  copies_delivrees: number; conservation: string; parcelle: string;
  surface_m2: number | null; mentions: number; mentions_vivantes: number;
}

export interface ListeFeuilles { feuilles: FeuilleResume[]; total: number; message?: string }

export interface MentionAffichee {
  rang: number; nature: string; acte: string; bordereau: string | null;
  inscriteLe: string; radieeLe: string | null; motifRadiation: string | null;
  vivante: boolean; quotite: string | null; natureDroit: string | null; rangDroit: number | null;
  depot: string | null; depotLe: string | null; document: string | null;
  /** ⚠️ `nin` est la MÊME rédaction que celle imprimée sur la copie du titre
   *  (`libelleNin`) : masquée, avec sa source et son état. Jamais le numéro. */
  titulaire: { id: string; denomination: string; type: string; nin?: string }
           | { masque: true; motif: string } | null;
}

export interface FicheFeuille {
  feuille: { numeroTitre: string; volume: string | null; folio: string | null;
             conservation: string; bureauRegistre: string | null; statut: string;
             ouverteLe: string; closeLe: string | null; motifCloture: string | null;
             copiesDelivrees: number };
  immeuble: { parcelle: string; referenceCadastrale: string | null; section: string | null;
              surfaceM2: number | null; division: string | null; statut: string };
  titre: { acteOrigine: string; dateEmission: string; autoriteEmettrice: string; statut: string }
       | { anomalie: string };
  mentions: MentionAffichee[];
  etatCourant: { mentions: number; lecture: string };
  dossierDuTitre: { pieces: { categorie: string; versee_le: string; identifiant: string;
                              type_code: string; emetteur: string; date_document: string }[];
                    lecture: string };
  copies: { delivrees: { rang: number; motif: string; delivree_le: string;
                         document: string | null; delivree_a: string | null }[]; lecture: string };
  depots: { volume: string; numero: number; nature_acte: string; objet_type: string;
            suite: string; motif_suite: string | null; depose_le: string }[];
  acces: { nominatif: boolean; sensibiliteAccordee: number };
}

export interface DepotAffiche {
  id: string; volume: string; numero: number; nature_acte: string;
  objet_type: string; objet_id: string | null; objet_reference: string | null;
  suite: string; motif_suite: string | null; depose_le: string; suite_le: string | null;
  conservation: string; numero_titre: string | null; deposant: string | null;
}

export interface ListeDepots {
  depots: DepotAffiche[];
  volumes: { volume: string; depots: number; premier: number; dernier: number;
             inscrits: number; rejetes: number; retires: number; en_instance: number }[];
  volumeCourant: string; lectureDesTrous: string; message?: string;
}

export interface ReponseLivre { message: string; [k: string]: unknown }
export interface ReponseDepot {
  depot: { id: string; volume: string; numero: number; reference: string; deposeLe: string };
  suite: string; message: string;
}
export interface ReponseCopie {
  titre: string; volume: string | null; folio: string | null; copie: number;
  document: string; rang: number; octets: number; empreinte: string; empreinteDonnees: string;
  jeton: string; urlVerification: string; urlTelechargement: string;
  avertissement?: string; message: string;
}

/**
 * ⚠️ « CE FONDS N'A PAS DE FEUILLE » EST UN RÉSULTAT, PAS UNE ERREUR — et c'est
 * la réponse la plus fréquente en Guinée. La route rend 404 avec un corps utile
 * portant `etat` ; `appelerVerification` le laisse passer au lieu de lever.
 */
export interface FeuilleDuFonds {
  etat: 'immatricule' | 'feuille_close' | 'non_immatricule';
  parcelle: string;
  feuille: { numeroTitre: string; volume: string | null; folio: string | null;
             statut: string; conservation: string } | null;
  message?: string;
}

export interface RequisitionDepuisActe {
  attestation: string; statut: string;
  requerable: boolean;
  /** Ce qui empêche, nommé — jamais un simple `false`. */
  empechements: string[];
  fourni: {
    immeuble: Record<string, unknown>;
    origineDetention: string; origineDetail: string; detientDepuis?: string;
    riverains: { cote: string; nom: string }[];
    piecesJointes: string[];
  };
  /** Ce que le registre ne connaît pas et ne fabriquera pas. */
  aDemander: string[];
  typeDemande: string;
  lecture: string;
}

export interface ReponseRequisition {
  attestation: string; demande: string; affaire: string;
  requerant: string; parcelle: string | null; serviceSaisi: string;
  message: string;
}


/* ── §20-§25 Prospective ────────────────────────────────────────────────── */

export interface CouvertureRelief {
  tuiles: number;
  volumeMonte: boolean;
  octets: number;
  altitudeMinM: number | null;
  altitudeMaxM: number | null;
  verseLe: string | null;
  source?: { libelle: string; licence: string; mentionAdaptee?: string; responsabilite?: string };
}

export type LectureAltitude =
  | { etat: 'mesure'; altitudeM: number; tuile: string; lat: number; lon: number; attribution?: string }
  | { etat: 'hors_couverture' | 'relief_absent'; tuile: string; lat: number; lon: number };

export interface ClassePente { cle: string; libelle: string; mailles: number; partPct: number }

export type LectureContraintes =
  | {
      etat: 'mesure';
      emprise: { ouest: number; sud: number; est: number; nord: number };
      surfaceHa: number; mailles: number;
      pasM: { x: number; y: number };
      tuiles: string[];
      altitude: { minM: number; maxM: number; moyenneM: number; denivelleM: number };
      pente: { moyennePct: number; maxPct: number; classes: ClassePente[]; urbanisablePct: number };
      eau: {
        cuvettes: number; pointsBasMailles: number; seuilPointBasM: number;
        surfaceEauPct: number; maillesTerre: number;
      };
      nonConnu: string[];
      attribution?: string;
      responsabilite?: string;
    }
  | { etat: 'relief_absent' }
  | { etat: 'hors_couverture'; tuilesManquantes: string[] }
  | { etat: 'emprise_invalide'; motif: string };


export interface NormeUrbaine {
  code: string; libelle: string; categorie: string;
  valeur: number; unite: string; referenceLegale: string; sourceEtablie: boolean;
}

export type LectureProgramme =
  | {
      etat: 'mesure';
      emprise: { ouest: number; sud: number; est: number; nord: number };
      occupation: {
        batiments: number; batimentsHa: number;
        parcelles: number; parcellesAvecDroit: number;
        partBatieDeLAmenageablePct: number;
      };
      terrain: {
        surfaceHa: number; surfaceEauPct: number; penteMoyennePct: number;
        penteMaxRetenuePct: number; amenageableHa: number; partAmenageablePct: number;
      };
      affectation: { code: string; libelle: string; partPct: number; surfaceHa: number }[];
      capacite: {
        logements: number; habitants: number; densiteBruteHabParHa: number;
        ecoles: number; centresSante: number;
      };
      /** ⚠️ Faux tant qu'une norme consommée n'est pas établie. */
      fondeEnDroit: boolean;
      normesNonEtablies: { code: string; libelle: string; referenceLegale: string }[];
      normes: NormeUrbaine[];
      avertissements: string[];
      attribution?: string;
    }
  | { etat: 'relief_absent' }
  | { etat: 'hors_couverture'; tuilesManquantes: string[] }
  | { etat: 'emprise_invalide'; motif: string }
  | { etat: 'normes_absentes'; manquantes: string[] };


export interface ParametresForme {
  niveaux: number; empriseAuSolPct: number; surfaceLogementM2: number;
  voiePrimaireM: number; voieSecondaireM: number; pasPrimaireM: number;
  pasSecondaireM: number; parcelleLargeurM: number; parcelleProfondeurM: number;
  orientationDeg: number; penteMaxPct: number;
}

export type LectureMasterplan =
  | {
      etat: 'mesure';
      emprise: { ouest: number; sud: number; est: number; nord: number };
      parametres: ParametresForme;
      bilan: {
        ilots: number; ilotsResidentiels: number; ilotsEspacesPublics: number; ilotsEquipements: number;
        parcelles: number; batiments: number;
        surfaceIlotsHa: number; surfaceParcellesHa: number;
        empriseAuSolHa: number; surfacePlancherHa: number;
        logements: number; habitants: number; densiteNetteLogParHa: number;
        lineaireVoiePrimaireM: number; lineaireVoieSecondaireM: number;
      };
      marchabilite: {
        vitesseKmh: number;
        parEquipement: { code: string; libelle: string; rayonM: number;
                         ilotsDesservisPct: number; distanceMoyenneM: number; tempsMoyenMin: number }[];
        partIlotsBienDesservisPct: number;
      };
      equipements: { code: string; libelle: string; famille: string;
                     requis: number; implantes: number; empriseM2: number }[];
      couches: {
        voies: GeoJSON.FeatureCollection; ilots: GeoJSON.FeatureCollection;
        parcelles: GeoJSON.FeatureCollection; batiments: GeoJSON.FeatureCollection;
        equipements: GeoJSON.FeatureCollection;
      };
      parcellaireOmis: boolean;
      fondeEnDroit: boolean;
      normesNonEtablies: { code: string; libelle: string; referenceLegale: string }[];
      avertissements: string[];
    }
  | { etat: 'relief_absent' }
  | { etat: 'hors_couverture'; tuilesManquantes: string[] }
  | { etat: 'emprise_invalide'; motif: string }
  | { etat: 'normes_absentes'; manquantes: string[] };

// ── La chaîne du §21 au complet (D.1491) — types des six moteurs ───────────

export interface EtatEtapes {
  etapes: { rang: number; nom: string; routes: string[]; servie: boolean }[];
  servies: number;
  total: number;
  absentes: string[];
}

export interface NormeNonEtablie { code: string; libelle: string; referenceLegale: string }

export interface LectureInfrastructures {
  etat: 'mesure' | 'normes_absentes';
  manquantes?: string[];
  habitants?: number; logements?: number;
  besoins?: { code: string; libelle: string; valeur: number; unite: string; calcul: string }[];
  ouvrages?: { code: string; libelle: string; requis: number; capaciteUnitaire: number; unite: string }[];
  fondeEnDroit?: boolean; normesNonEtablies?: NormeNonEtablie[]; avertissements?: string[];
}

export interface LectureMobilite {
  etat: 'mesure' | 'normes_absentes';
  manquantes?: string[];
  reseau?: { lineairePrimaireM: number; lineaireSecondaireM: number; densiteMaillageKmKm2: number;
             intersectionsPrimaires: number; partVoiriePct: number };
  transportCollectif?: { arrets: number; espacementM: number; rayonAccesM: number;
    ilotsDesservisPct: number; logementsDesservisPct: number;
    tempsMoyenVersArretMin: number; objectifPct: number; objectifAtteint: boolean };
  tempsVersCentre?: { aPiedMin: number; enVehiculeMin: number; vitesseMarcheKmh: number; vitesseVehiculeKmh: number };
  couches?: { arrets: GeoJSON.FeatureCollection };
  fondeEnDroit?: boolean; normesNonEtablies?: NormeNonEtablie[]; avertissements?: string[];
}

export interface LectureQuartiers {
  etat: 'mesure' | 'sans_sieges';
  motif?: string;
  quartiers?: { id: string; siege: { code: string; libelle: string }; ilots: number;
    ilotsResidentiels: number; habitants: number; surfaceHa: number;
    equipements: { code: string; libelle: string; nombre: number }[];
    manquants: { code: string; libelle: string }[]; rayonVecuM: number }[];
  quartiersComplets?: number; partQuartiersCompletsPct?: number; avertissements?: string[];
}

export interface LectureBatiments {
  etat: 'mesure' | 'normes_absentes' | 'parcellaire_omis';
  manquantes?: string[]; motif?: string;
  typologie?: { residentiels: number; mixtes: number; plancherTotalM2: number;
    plancherLogementsM2: number; plancherCommercesM2: number; logements: number;
    logementsPerdusAuProfitDesCommerces: number; commerces: number };
  fondeEnDroit?: boolean; normesNonEtablies?: NormeNonEtablie[]; avertissements?: string[];
}

export interface LectureReseaux {
  etat: 'mesure' | 'normes_absentes' | 'besoins_absents';
  manquantes?: string[]; motif?: string;
  lineaireVoirieM?: number;
  reseaux?: { code: string; libelle: string; lineaireM: number; charge: string;
    points: { libelle: string; nombre: number } | null; coutOrdreGrandeurGNF: number | null }[];
  drainage?: { penteMoyennePct: number; penteMinNormeePct: number; gravitaire: boolean; lecture: string };
  coutTotalGNF?: number | null;
  fondeEnDroit?: boolean; normesNonEtablies?: NormeNonEtablie[]; avertissements?: string[];
}

export interface LectureSimulation {
  etat: 'mesure' | 'relief_absent' | 'hors_couverture' | 'emprise_invalide' | 'normes_absentes';
  tuilesManquantes?: string[]; motif?: string; manquantes?: string[];
  emprise?: { ouest: number; sud: number; est: number; nord: number };
  viabilite?: { viable: boolean; lecture: string;
    essentiels: { code: string; libelle: string; couvert: boolean; mesure: string; manque: string | null }[] };
  climat?: {
    inondation: { cuvettes: number; pointsBasMailles: number; surfaceEauPct: number; lecture: string };
    impermeabilisationPct: number; partEspacesPublicsPct: number; lecture: string };
  indicateurs?: { code: string; libelle: string; valeur: number; unite: string;
    source: string; confiance: string }[];
  etapes?: {
    contraintes: LectureContraintes;
    programme: LectureProgramme;
    plan: LectureMasterplan;
    infrastructures: LectureInfrastructures;
    mobilite: LectureMobilite;
    quartiers: LectureQuartiers;
    batiments: LectureBatiments;
    reseaux: LectureReseaux;
  };
  fondeEnDroit?: boolean; avertissements?: string[];
  attribution?: string;
}

export interface ScenarioResume {
  reference: string; titre: string; mode: 'creer' | 'transformer' | 'simuler';
  statut: 'actif' | 'retire';
  emprise: { ouest: number; sud: number; est: number; nord: number };
  creeLe: string; viable: boolean | null;
}

export interface FicheScenario {
  reference: string; titre: string; mode: string; statut: string;
  ouest: number; sud: number; est: number; nord: number;
  parametres: Record<string, number>; hypotheses: string;
  bilan: {
    viabilite: NonNullable<LectureSimulation['viabilite']>;
    climat: NonNullable<LectureSimulation['climat']>;
    indicateurs: NonNullable<LectureSimulation['indicateurs']>;
    bilanPlan: Record<string, number>;
    fondeEnDroit: boolean;
    avertissements: string[];
  };
  bilan_calcule_le: string; cree_le: string; retire_le: string | null;
}

/**
 * LA PROJECTION D'UN ACTE — §62 · §63 · §64.
 *
 * ⚠️ CE TYPE EST REDÉCLARÉ ICI ET NON IMPORTÉ DE `src/domaine`. Ce n'est pas
 * une négligence : l'étage `interface` du Dockerfile ne copie que `frontend/`
 * et `src/partage/`. Un import depuis `src/domaine` compile en local — l'arbre
 * entier est là — et **casse le build Docker**, défaut consigné en tête de
 * `src/partage/quotites.ts` et payé une fois.
 */
export interface DroitProjete {
  id: string;
  nature: string;
  titulaire: string;
  quotite: { num: number; den: number };
  rang: number | null;
}

export interface Projection {
  mutation: string;
  type: string;
  statut: string;
  deja_appliquee: boolean;
  applique_le?: string;
  lecture: string;
  /** Absents quand l'acte a déjà produit ses effets : il n'y a rien à projeter. */
  avant?: DroitProjete[];
  apres?: DroitProjete[];
  operation?: { radies: DroitProjete[]; inscrits: DroitProjete[]; survivants: DroitProjete[] };
  quotites?: { avant: SommeAffichee; apres: SommeAffichee };
  impacts?: Array<{ code: string; niveau: 'bloquant' | 'eleve' | 'attention' | 'info'; texte: string }>;
  jouable?: boolean;
  refus?: string | null;
}

export interface SommeAffichee {
  num: number; den: number; complet: boolean; excedentaire: boolean;
  reste: { num: number; den: number } | null; parts: number;
}

/** §50 — la fiche d'une succession, telle que le service la rend. */
export interface Succession {
  succession: { reference: string; defunt: string; date_deces: string; statut: string;
                /**
                 * ⚠️ LA RÉFÉRENCE DU DOSSIER, ET NON SON UUID. Sans elle,
                 * l'écran ne peut pas rattacher l'acte déposé au dossier ouvert
                 * au guichet — et `documents.ts` retrouve le formulaire d'un
                 * acte par `mutation → dossier → demande de même type`. Un acte
                 * de dévolution non rattaché s'imprime sans aucun état civil du
                 * défunt, sans la moindre erreur.
                 */
                affaire: string | null;
                [k: string]: unknown };
  ayantsDroit: Array<{ id: string; denomination: string; qualite: string; lien: string | null;
                       vocation_num: number | null; vocation_den: number | null;
                       option: string; option_le: string | null; source: string;
                       ecarte_le: string | null; motif_ecart: string | null }>;
  masse: Array<{ id: string; nature: string; categorie: string; designation: string;
                 valeur: string | null; devise: string | null; source_valeur: string | null }>;
  liquidation: { actif: number; passif: number; net: number; lecture?: string };
  vocation: { complete?: boolean; num?: number; den?: number; [k: string]: unknown };
  /** Ce que le REGISTRE constate au nom du défunt — la seule part opposable. */
  fondsInscritsAuDefunt: Array<{ parcelle: string; nature: string;
                                 quotite_num: number; quotite_den: number }>;
}

/** §53 — le projet de partage, composé par le service. */
export interface ProjetPartage {
  succession: string;
  defunt: string;
  dateDeces: string;
  acceptants: number;
  fondsDuDefunt: Array<{ parcelle: string; [k: string]: unknown }>;
  projets: Array<Record<string, unknown>>;
  /** Ce qui EMPÊCHE de déposer — nommé, jamais résumé en un booléen. */
  empechements: string[];
  pretADeposer: boolean;
  lecture?: string;
}

/**
 * §43 — l'état des signatures d'un acte, tel que le service le rend.
 *
 * ⚠️ `correspondALActe` EST LE CHAMP QUI COMPTE, et il n'est pas décoratif.
 * On ne signe pas un document, on signe une EMPREINTE (migration 160) : si
 * l'acte change d'un octet, la signature cesse de lui correspondre et devient
 * détectablement caduque. Un écran qui afficherait « signé » sans ce champ
 * présenterait comme valide un acte signé sur une version disparue.
 */
export interface EtatSignatures {
  document: string;
  type: string;
  empreinteCourante: string;
  etat: Record<string, unknown>;
  signatures: Array<{
    id: string; role: string; qualite: string | null; procede: string;
    horodate_le: string; lieu: string | null; statut: string;
    motif_revocation: string | null; certificat_kid: string | null;
    correspond_a_l_acte: boolean; signataire: string | null;
    preuve_deposee: boolean;
  }>;
  scellement: {
    scelle_le: string; mention: string | null; correspond_a_l_acte: boolean;
    scelle_par: string | null; organisation: string | null;
  } | null;
}

/**
 * §2 du cahier de navigation — la liste des mutations et ses COMPTES.
 *
 * ⚠️ LES COMPTES PORTENT SUR LA POPULATION, PAS SUR LA PAGE, et ils sont
 * cloisonnés par le même mandat que la liste. Les recalculer ici sur
 * `mutations` rendrait « 3 ventes » sur un registre qui en porte trois cents —
 * la page est tronquée à 50, et l'écran afficherait ce chiffre avec l'assurance
 * d'un total.
 */
export interface ListeMutations {
  mutations: Mutation[];
  tronque: boolean;
  limite: number;
  portee: 'territoire' | 'mandat';
  comptes: { parType: Record<string, number>; parStatut: Record<string, number> };
}


/* ═══════════════════════════════════════════════════════════════════════════
 * LES TYPES DE L'AFFAIRE
 *
 * ⚠️ `empechement` N'EST JAMAIS `undefined` QUAND `ouvert` EST FAUX. Un geste
 * fermé SANS motif est un bouton grisé sans raison — et un bouton grisé sans
 * raison se lit « le produit est cassé ». Le service garantit le couple.
 * ═══════════════════════════════════════════════════════════════════════════ */

export interface GesteAffaire {
  geste: string; sens: string; vers: string; versLibelle: string;
  ouvert: boolean; aide: string | null;
  empechement: { code: string; motif: string; remede: string; details?: unknown } | null;
}

export interface MessageAffaire {
  id: string; auteur: string;
  portee: 'tous' | 'metier' | 'agent' | 'service';
  porteeCible: string | null;
  deposeLe: string;
  /** `null` quand le message est couvert par sa portée — jamais « vide ». */
  contenu: string | null;
  couvert: boolean;
}

export interface EtapeParcours {
  code: string; libelle: string; quoi: string; consigne: string | null;
  mode: string | null; rang: number; delai_jours: number | null; terminal: boolean;
  position: 'faite' | 'courante' | 'a_venir';
  /** Le texte qui fonde l'étape. `null` = étape non opposable, et l'écran le DIT. */
  fondement_legal: string | null;
}

export interface PieceAffaire {
  code: string; libelle: string; aide: string; obligatoire: boolean;
  statut: 'attendue' | 'deposee' | 'acceptee' | 'refusee';
  document_id: string | null; deposee_le: string | null; motif_refus: string | null;
  /**
   * L'identifiant LISIBLE du fichier versé — `GN-DOC-…`, celui qui se met dans
   * un lien. `document_id` est un UUID : il ne sert à rien à l'écran.
   *
   * ⚠️ SANS LUI, « Accepter » ÉTAIT UN GESTE AVEUGLE. L'agent voyait l'intitulé
   * de la pièce et deux boutons, sans pouvoir ouvrir le fichier : accepter
   * revenait à valider sur la foi du nom, refuser à refuser sans constat.
   */
  document_identifiant?: string | null;
  document_type_mime?: string | null;
  document_octets?: number | null;
  /**
   * Versée alors que l'étape courante ne la réclame pas.
   * ⚠️ Elle DOIT s'afficher : une pièce en base et absente de l'écran fait
   * verser une seconde fois, puis conclure que le bouton ne marche pas.
   */
  hors_liste?: boolean;
}

export interface ControleAffaire {
  regle: string; libelle: string; bloquant: boolean;
  resultat: 'non_joue' | 'conforme' | 'anomalie' | 'non_evaluable';
  candidats_examines: number | null; traite_le: string | null;
  traite_decision: string | null; traite_motif: string | null;
}

export interface ParcoursAffaire {
  affaire: { reference: string; referenceAnterieure: string | null; type: string;
             etat: string; etape: string; mode: string | null; priorite: string };
  procedure: { code: string; version: number; libelle: string; objet: string } | null;
  parcours: EtapeParcours[];
  ici: { etape: string; libelle: string; rang: number; sur: number;
         consigne: string | null; sansConsigne: string | null } | null;
  gestes: GesteAffaire[];
  pieces: { liste: PieceAffaire[]; manquantes: number };
  controles: { liste: ControleAffaire[]; bloquants: number };
  affectation: { issue: string; affectee_le: string; prise_en_charge_le: string | null;
                 affectee_par: string; agent: string; c_est_moi: boolean } | null;
  complementOuvert: { motif: string; pieces_attendues: string[];
                      demande_le: string; echeance: string | null } | null;
  /**
   * Les collègues de ce service qui peuvent SIGNER l'acte que je décide.
   * ⚠️ Vide ne veut pas dire « personne ne signe » : cela veut dire que ce
   * service ne peut pas franchir seul une étape à signature (D.848).
   */
  signatairesPossibles: { compte_id: string; nom: string }[];
  delai: { echeance: string | null; suspendu: boolean;
           pourquoiSuspendu: string | null; consommeJours: number };
}

export interface ChronologieAffaire {
  affaire: string;
  etapes: { etape: string; etape_libelle: string | null; survenu_le: string;
            commentaire: string | null; detail: Record<string, unknown>; acteur: string | null }[];
  decisions: { etape: string; sens: string; motif: string; survenu_le: string;
               mode_autorite: string; agent: string; organisation: string; signee: boolean }[];
  transmissions: { de_mode: string; vers_mode: string; etape_source: string; etape_cible: string;
                   motif: string; expediee_le: string; accuse_le: string | null;
                   snapshot_version: number | null; empreinte_sha256: string | null }[];
  pieces: { code_piece: string; statut: string; deposee_le: string | null;
            statuee_le: string | null; motif_refus: string | null }[];
  sansAccuse: number;
}

/**
 * ⚠️ `type_code` EST UN CODE DE WORKFLOW (`WF-003`), PAS LE NOM DE LA DÉMARCHE.
 * `procedure_libelle` porte le nom lisible, tiré de `fon.procedure`. Afficher
 * le premier sur un poste d'État demanderait à l'agent de connaître le
 * catalogue des procédures — §68.
 */
export interface LigneAffaire {
  reference: string; type_code: string; procedure_libelle: string;
  objet: string | null; requerant: string | null; parcelle: string | null;
  etape_courante: string; etape_libelle: string | null; consigne: string | null;
  priorite: string; en_retard: boolean; echeance_etape?: string | null;
}
export interface MonBureau {
  service: string | null; modes: string[];
  aTraiter: (LigneAffaire & { etat: string; issue: string; affectee_le: string;
              prise_en_charge_le: string | null })[];
  enFileDuService: number;
  message: string | null;
}

export interface FileService {
  service: string; modes: string[];
  /** ⚠️ `candidats: 0` n'est pas « rien à faire » : c'est « personne ne peut la prendre ». */
  files: Record<string, (LigneAffaire & { entree_file_le: string; attente_jours: number;
                          candidats: number })[]>;
  total: number; sansCandidat: number; alerte: string | null;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * LE FORMULAIRE DE L'ACTE — types d'interface
 *
 * ⚠️⚠️ REDÉCLARÉS ICI, ET CE N'EST PAS UN OUBLI. L'étage `interface` du
 * Dockerfile ne reçoit que `frontend/` et `src/partage` : un
 * `import type` vers `src/domaine/complement-acte.js` compile sur ce poste —
 * l'arbre complet est sur le disque — et rend `TS2307` dans le conteneur,
 * plusieurs minutes plus tard, à l'étape 5 d'une bascule. Le piège a été payé
 * deux fois dans ce dépôt ; `tests/frontiere-etage-interface.test.ts` est la
 * porte qui le referme.
 * ═══════════════════════════════════════════════════════════════════════════ */

export type TypeChampActe =
  | 'texte' | 'texte_long' | 'date' | 'nombre' | 'booleen' | 'liste'
  | 'telephone' | 'courriel';

export interface ChampActe {
  cle: string;
  /** Chemin ABSOLU dans le formulaire : `defunt.personne.pere.nom`. */
  chemin: string;
  libelle: string;
  type: TypeChampActe;
  options?: { valeur: string; libelle: string }[];
  aide?: string;
  max?: number;
  valeur?: unknown;
  /** D'où vient la valeur affichée. Absent = personne ne l'a fournie. */
  source?: 'depot' | 'complement' | 'identite' | 'constat';
}

export interface SectionActe {
  cle: string;
  titre: string;
  aide?: string;
  repetable: boolean;
  blocs: { chemin: string; titre: string; champs: ChampActe[] }[];
  gabarit?: Omit<ChampActe, 'chemin' | 'valeur' | 'source'>[];
}

export interface FormulaireActe {
  affaire: string;
  /**
   * Le droit `fon.affaire:traiter` sur cette affaire — compléter l'acte ET
   * verser une pièce.
   *
   * ⚠️ DÉCIDÉ PAR LE SERVICE, JAMAIS PAR L'ÉCRAN : voir la route. Et il
   * s'appelle comme le DROIT, pas comme l'un des deux gestes qu'il gouverne :
   * `peutCompleter` aurait fini par autoriser le versement sous un nom qui ne
   * le nomme pas.
   */
  peutTraiterAffaire: boolean;
  etat?: string;
  acte: { type: string; libelle: string } | null;
  /** Le type que porte l'affaire, même quand il ne produit aucun acte. */
  procedure: { code: string; libelle: string | null; provenance: 'mutation' | 'demande' } | null;
  /** Renseigné quand l'affaire ne porte aucun acte : dire POURQUOI, pas rendre vide. */
  pourquoi?: string | null;
  depot: { reference: string } | null;
  complement: { version: number; motif: string; saisiLe: string; saisiPar: string | null } | null;
  version: number;
  sections: SectionActe[];
  manquants: number;
  total: number;
  etablies?: number;
  complete: number;
  avertissement: string | null;
}

export interface ResultatTeleversement {
  affaire: string;
  piece: { code: string; statut: string };
  document: { id: string; identifiant: string; version: number };
  empreinteSha256: string;
  tailleOctets: number;
  deduplique: boolean;
  indexation: { statut: string; motif?: string };
  message: string;
}


// ───────────────────────────────────────────────────────────────────────────
// D.926 · D.927 · D.930 — les contrats des trois portes d'entrée du levé
// ───────────────────────────────────────────────────────────────────────────

export interface CorpsImport {
  /** Shapefile en base64. Exclusif avec `dxf`. */
  shpBase64?: string;
  dbfBase64?: string;
  prj?: string;
  /** DXF ASCII en clair. Exclusif avec `shpBase64`. */
  dxf?: string;
  /**
   * ⚠️ OBLIGATOIRE POUR UN DXF : ce format ne porte AUCUN système de
   * coordonnées. Pour un Shapefile, il ne sert qu'en l'absence de `.prj`.
   */
  epsg?: number;
  calques?: string[];
}

export interface EntiteAnalysee {
  rang: number;
  attributs: Record<string, string | number | null>;
  surfaceM2: number;
  geometrieValide: boolean;
  raisonInvalidite: string | null;
  recouvrements: Array<{ parcelle: string; partPct: number }>;
}

export interface AnalyseImport {
  systeme: { epsg: number; nom: string; origine: 'prj' | 'impose' };
  entites: number;
  surfaceTotaleM2: number;
  avertissements: string[];
  entitesEnRecouvrement: number;
  entitesInvalides: number;
  apercu: EntiteAnalysee[];
}

export interface DepotImport {
  lotReference: string;
  methode: MethodeLeve;
  precisionAnnonceeM: number;
  dossierReference?: string;
  observations?: string;
  malgreRecouvrements?: boolean;
}

export interface DepotImportFait {
  lot: string;
  systeme: { epsg: number; nom: string };
  entitesLues: number;
  deposes: number;
  refuses: number;
  avertissements: string[];
  leves: Array<{ rang: number; reference: string; recevable: boolean; surfaceM2: number }>;
  entitesRefusees: Array<{ rang: number; raison: string }>;
}

export interface LotImport { lot: string; leves: number; depose_le: string; statuts: string }

export type MethodeLeve = 'gnss_rtk' | 'gnss_statique' | 'station_totale' | 'gps_navigation' | 'photo_interpretation';
export type MethodeCalcul = 'bowditch' | 'moindres_carres';
export type UniteAngle = 'grade' | 'degre';

export interface LigneCheminement {
  de: string;
  vers: string;
  gisement: number;
  distance: number;
}

export interface Cheminement {
  unite: UniteAngle;
  depart: { nom: string; x: number; y: number; zone: number };
  lignes: LigneCheminement[];
}

export interface DerouleCogo {
  points: Array<{ nom: string; x: number; y: number; longitude: number; latitude: number }>;
  perimetreM: number;
  surfaceM2: number;
  fermeture: { dEst: number; dNord: number; ecartM: number; perimetreM: number; precisionRelative: number | null } | null;
  toleranceM: number;
  dansLaTolerance: boolean | null;
  /** ⚠️ Un cheminement ouvert n'a PAS de fermeture — et « pas de fermeture » ne se lit jamais « fermeture parfaite ». */
  boucle: boolean;
}

export interface ResiduCogo {
  type: 'distance' | 'gisement';
  de: string; vers: string;
  residu: number;
  residuNormalise: number;
  suspecte: boolean;
}

export interface AjustementCogo {
  methode: MethodeCalcul;
  points: Array<{ nom: string; x: number; y: number; longitude: number; latitude: number; fixe: boolean;
                  ellipse: { demiGrandAxeM: number; demiPetitAxeM: number; orientation: number } | null }>;
  surfaceAvantM2: number;
  surfaceApresM2: number;
  fermetureAvantM: number | null;
  toleranceM: number;
  dansLaTolerance: boolean | null;
  /** ⚠️ `null` quand le degré de liberté est nul : sans redondance, RIEN n'est éprouvé. */
  degreLiberte: number | null;
  sigmaZero: number | null;
  testGlobalPasse: boolean | null;
  suspectes: ResiduCogo[];
  residus: ResiduCogo[];
  avertissement: string | null;
}

export interface DepotCogo {
  methodeCalcul: MethodeCalcul;
  methode: MethodeLeve;
  precisionAnnonceeM: number;
  fixes?: string[];
  dossierReference?: string;
  sourceActe?: string;
  observations?: string;
}

export interface DepotCogoFait {
  releve: { id: string; reference: string; surfaceM2: number };
  lignesEnregistrees: number;
  fermetureM: number | null;
  toleranceM: number;
  degreLiberte: number | null;
  testGlobalPasse: boolean | null;
  suspectes: number;
  recevable: boolean;
}

export interface FicheCogo {
  fiche: {
    reference: string; statut: string; lignes: number; unite_angle: string | null;
    perimetre_declare_m: number | null; methode: string | null;
    ecart_fermeture_m: number | null; tolerance_m: number | null; precision_relative: number | null;
    degre_liberte: number | null; sigma_zero: number | null; test_global: boolean | null;
    observations_suspectes: number; verdict: string;
  };
  lignes: Array<{ rang: number; point_de: string; point_vers: string; unite_angle: string;
                  gisement: number; distance_m: number; source_acte: string | null }>;
  points: Array<{ nom: string; x_m: number; y_m: number; epsg: number; fixe: boolean;
                  demi_grand_axe_m: number | null; demi_petit_axe_m: number | null; orientation: number | null }>;
}

export interface OperationTerrain {
  cleAppareil: string;
  objetType: 'borne' | 'riverain' | 'observation' | 'photo';
  objetCle: string;
  verbe: 'creer' | 'modifier' | 'supprimer';
  versionVue?: number;
  saisieLe: string;
  charge: Record<string, unknown>;
}

export interface LotTerrain {
  appareil: string;
  leveReference: string;
  operations: OperationTerrain[];
}

export interface RemonteeFaite {
  leve: string;
  recues: number;
  appliquees: number;
  dejaVues: number;
  conflits: number;
  refusees: number;
  /** ⚠️ Ce que l'appareil a le DROIT d'effacer. La règle vit au service, pas à l'écran. */
  effacables: string[];
  detail: Array<{ cleAppareil: string; etat: string; motif: string | null }>;
}

export interface OperationEnSouffrance {
  appareil: string; objet_type: string; objet_cle: string; verbe: string;
  etat: string; motif: string | null; saisie_le: string; recue_le: string;
}
